import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, UserCheck, Settings, FileText, Calendar, MessageSquare, Shield, Home, LogOut, Edit, Menu, X, Database, FileEdit, Globe, RefreshCw, Download, Upload, Image, Trash2, Zap, Mail, Code, Copy } from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentSection = location.pathname.split('/')[2] || 'dashboard';
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const menuItems = [
    { id: 'dashboard', icon: Home, label: 'Tableau de bord', description: 'Vue d\'ensemble' },
    { id: 'members', icon: Users, label: 'Gestion des membres', description: 'Ajouter, éditer, gérer les membres' },
    { id: 'database', icon: Database, label: 'Database', description: 'Édition directe des données' },
    { id: 'cotisations', icon: UserCheck, label: 'Gestion des cotisations', description: 'Paiements et cotisations' },
    { id: 'site', icon: FileEdit, label: 'Gestion du site', description: 'Textes statiques et pages' },
    { id: 'translations', icon: Globe, label: 'Traductions', description: 'Traduire le site automatiquement' },
    { id: 'news', icon: FileText, label: 'News/Blog', description: 'Articles et actualités' },
    { id: 'newsletter', icon: Mail, label: 'Newsletter', description: 'Gestion des abonnés newsletter' },
    { id: 'publications', icon: FileText, label: 'Publications', description: 'Bulletins, documents' },
    { id: 'events', icon: Calendar, label: 'Événements', description: 'Gestion des événements' },
    { id: 'albums', icon: Image, label: 'Albums Photos', description: 'Gestion des albums photos' },
    { id: 'familles', icon: Shield, label: 'Familles et blasons', description: 'Base de données généalogique' },
    { id: 'forum', icon: MessageSquare, label: 'Forum', description: 'Modération du forum' },
    { id: 'finances', icon: UserCheck, label: 'Finances', description: 'Paiements Stripe' },
    { id: 'settings', icon: Settings, label: 'Paramètres', description: 'Configuration système (Export/Import BD)' },
    { id: 'backup', icon: Download, label: 'Backup', description: 'Sauvegarde complète du site' }
  ];

  const [stats, setStats] = useState([
    { title: 'Total Membres', value: '...', subtitle: 'Chargement...', color: 'blue' },
    { title: 'Cotisations 2025', value: '...', subtitle: 'Chargement...', color: 'green' },
    { title: 'Événements', value: '...', subtitle: 'Chargement...', color: 'purple' },
    { title: 'Publications', value: '...', subtitle: 'Chargement...', color: 'orange' },
  ]);

  useEffect(() => {
    // Vérifier l'authentification au chargement
    const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
    const currentUser = localStorage.getItem('currentUser');
    
    if (token) {
      // Vérifier que la session est toujours valide côté serveur
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }).then(response => {
        if (response.status === 401 || response.status === 403) {
          // Session expirée - nettoyer et afficher le formulaire de login
          console.warn('Session expirée');
          localStorage.removeItem('authToken');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('currentUser');
          setIsAuthenticated(false);
        } else if (response.ok) {
          // Session valide
          setIsAuthenticated(true);
          
          if (currentUser) {
            try {
              const userData = JSON.parse(currentUser);
              setUser({ 
                name: 'Administrateur', 
                email: userData.email 
              });
            } catch (e) {
              setUser({ name: 'Administrateur', email: 'admin@lignages.be' });
            }
          } else {
            setUser({ name: 'Administrateur', email: 'admin@lignages.be' });
          }
          
          // Charger les statistiques réelles
          fetchRealStats();
        }
      }).catch(err => {
        console.error('Erreur vérification session:', err);
        // En cas d'erreur réseau, nettoyer et afficher le formulaire de login
        localStorage.removeItem('authToken');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('currentUser');
        setIsAuthenticated(false);
      });
    } else {
      // Pas de token - afficher le formulaire de login (pas de redirection)
      setIsAuthenticated(false);
    }
  }, []);

  const fetchRealStats = async () => {
    const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
    
    console.log('🔄 Chargement des statistiques réelles...');
    
    let totalMembers = 0;
    let cotisationsStats = { paid: 0, total: 0, revenue: 0 };
    let eventsStats = { total: 0, upcoming: 0 };
    let publicationsStats = { total: 0, thisYear: 0 };
    
    // Récupérer le nombre total de membres
    try {
      const membersResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/users?limit=1000`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (membersResponse.ok) {
        const members = await membersResponse.json();
        totalMembers = members.length;
        console.log(`✅ ${totalMembers} membres trouvés`);
      }
    } catch (error) {
      console.error('❌ Erreur chargement membres:', error);
    }

    // Déterminer l'année à afficher (année courante ou suivante si on est en décembre)
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    const displayYear = currentMonth >= 12 ? currentYear + 1 : currentYear;

    // Récupérer les statistiques de cotisations pour l'année affichée
    try {
      const cotisationsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations/annuelle?year=${displayYear}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (cotisationsResponse.ok) {
        const cotisationsData = await cotisationsResponse.json();
        if (cotisationsData.cotisations) {
          cotisationsStats.total = cotisationsData.cotisations.length;
          cotisationsStats.paid = cotisationsData.cotisations.filter(c => c.status === 'payee').length;
          cotisationsStats.revenue = cotisationsStats.paid * 30; // 30€ par cotisation
        }
        console.log('✅ Statistiques cotisations chargées');
      }
    } catch (error) {
      console.error('❌ Erreur chargement cotisations:', error);
    }

    // Récupérer les statistiques des événements
    try {
      const eventsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/events`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (eventsResponse.ok) {
        const eventsData = await eventsResponse.json();
        const events = eventsData.events || [];
        eventsStats.total = events.length;
        
        // Compter les événements à venir
        const nowDate = new Date();
        eventsStats.upcoming = events.filter(event => {
          const eventDate = new Date(event.start_datetime);
          return eventDate > nowDate;
        }).length;
        
        console.log(`✅ ${eventsStats.total} événements trouvés (${eventsStats.upcoming} à venir)`);
      }
    } catch (error) {
      console.error('❌ Erreur chargement événements:', error);
    }

    // Récupérer les statistiques des publications
    try {
      const publicationsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/publications`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (publicationsResponse.ok) {
        const publicationsData = await publicationsResponse.json();
        const publications = publicationsData.publications || [];
        publicationsStats.total = publications.length;
        
        // Compter les publications de cette année
        const pubYear = new Date().getFullYear();
        publicationsStats.thisYear = publications.filter(pub => {
          if (pub.created_at) {
            const createdYear = new Date(pub.created_at).getFullYear();
            return createdYear === pubYear;
          }
          return false;
        }).length;
        
        console.log(`✅ ${publicationsStats.total} publications trouvées (${publicationsStats.thisYear} cette année)`);
      }
    } catch (error) {
      console.error('❌ Erreur chargement publications:', error);
    }

    // Calculer les nouveaux membres (approximation)
    const newMembersThisMonth = Math.floor(totalMembers * 0.02); // 2% approximatif

    // Mettre à jour les statistiques
    setStats([
      { 
        title: 'Total Membres', 
        value: totalMembers > 0 ? totalMembers.toString() : 'Erreur', 
        subtitle: totalMembers > 0 ? `${newMembersThisMonth} nouveaux ce mois` : 'Impossible de charger', 
        color: 'blue' 
      },
      { 
        title: `Cotisations ${displayYear}`, 
        value: cotisationsStats.total > 0 ? `€${cotisationsStats.revenue.toLocaleString()}` : 'Erreur', 
        subtitle: cotisationsStats.total > 0 ? `${cotisationsStats.paid}/${cotisationsStats.total} payées` : 'Impossible de charger', 
        color: 'green' 
      },
      { 
        title: 'Événements', 
        value: eventsStats.total.toString(), 
        subtitle: `${eventsStats.upcoming} à venir`, 
        color: 'purple' 
      },
      { 
        title: 'Publications', 
        value: publicationsStats.total > 0 ? publicationsStats.total.toString() : 'Erreur', 
        subtitle: publicationsStats.total > 0 ? `${publicationsStats.thisYear} cette année` : 'Impossible de charger', 
        color: 'orange' 
      },
    ]);

    console.log('✅ Statistiques mises à jour dans le tableau de bord');
  };

  const handleLogin = async (email, password) => {
    try {
      // Normalisation anti-espace/casse
      const normalize = (s) => (s || '').replace(/\u00A0/g, ' ').trim();
      const normalizedEmail = normalize(email).toLowerCase();
      const normalizedPassword = normalize(password);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: normalizedEmail, password: normalizedPassword }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.user_role === 'admin') {
          
          // Récupérer le profil complet de l'utilisateur (incluant is_moderator)
          const profileResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${data.access_token}`
            }
          });
          
          let userProfile = null;
          if (profileResponse.ok) {
            userProfile = await profileResponse.json();
          }
          
          // Utiliser un token unifié
          localStorage.setItem('authToken', data.access_token);
          localStorage.setItem('adminToken', data.access_token); // Compatibilité
          localStorage.setItem('currentUser', JSON.stringify({
            id: userProfile?.id,
            email: normalizedEmail,
            role: 'admin',
            name: 'Admin',
            token: data.access_token,
            is_moderator: userProfile?.is_moderator || false
          }));
          
          setIsAuthenticated(true);
          setUser({ 
            name: 'Administrateur', 
            email: normalizedEmail,
            is_moderator: userProfile?.is_moderator || false
          });
        } else {
          alert('Accès administrateur requis');
        }
      } else {
        alert('Identifiants incorrects');
      }
    } catch (error) {
      console.error('Erreur de connexion:', error);
      alert('Erreur de connexion');
    }
  };

  const handleLogout = () => {
    // Nettoyer TOUS les tokens
    localStorage.removeItem('adminToken');
    localStorage.removeItem('memberToken');
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    setIsAuthenticated(false);
    setUser(null);
    navigate('/admin');
  };

  if (!isAuthenticated) {
    return <LoginForm onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      {/* Mobile Header */}
      <div className="md:hidden bg-white shadow-md p-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <img 
            src="/images/lignages-logo-admin.png" 
            alt="Logo Lignages" 
            className="w-8 h-8 object-contain"
          />
          <h1 className="text-lg font-bold" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
            Administration
          </h1>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      {/* Sidebar Desktop */}
      <div className="hidden md:flex md:w-64 bg-white shadow-lg flex-shrink-0 relative flex-col">
        <div className="p-6 border-b">
          <div className="flex items-center space-x-3">
            <img 
              src="/images/lignages-logo-admin.png" 
              alt="Logo Lignages" 
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="text-xl font-bold" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
                Administration
              </h1>
              <p className="text-sm text-gray-600">Lignages de Bruxelles</p>
            </div>
          </div>
          
          {/* Bouton Retour au site - En haut du menu */}
          <a 
            href="/" 
            className="mt-4 flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
            style={{ fontFamily: '"Lora", serif' }}
          >
            <Home className="w-4 h-4" style={{ color: 'rgb(196, 150, 96)' }} />
            <span className="text-sm font-medium" style={{ color: 'rgb(196, 150, 96)' }}>
              Retour au site
            </span>
          </a>
        </div>

        <nav className="p-4 flex-1 overflow-y-auto">
          <div className="space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => navigate(`/admin/${item.id}`)}
                className={`w-full text-left p-3 rounded-lg transition-all hover:bg-gray-50 ${
                  currentSection === item.id ? 'bg-gray-100' : ''
                }`}
                style={{ 
                  fontFamily: '"Lora", serif',
                  borderLeft: currentSection === item.id ? '3px solid rgb(196, 150, 96)' : '3px solid transparent'
                }}
              >
                <div className="flex items-center space-x-3">
                  <item.icon 
                    className="w-5 h-5" 
                    style={{ color: currentSection === item.id ? 'rgb(196, 150, 96)' : '#6b7280' }} 
                  />
                  <div>
                    <div 
                      className="font-medium"
                      style={{ color: currentSection === item.id ? 'rgb(196, 150, 96)' : '#374151' }}
                    >
                      {item.label}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Bouton Espace Membres */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <a 
              href="/membre" 
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
              style={{ fontFamily: '"Lora", serif' }}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
              </svg>
              <span className="text-sm font-medium">Accéder à l'Espace Membres</span>
            </a>
          </div>
        </nav>

        <div className="p-4">
          <div className="bg-gray-100 rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user.name}</p>
                <p className="text-xs text-gray-600 truncate">{user.email}</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleLogout}
                className="hover:bg-gray-200 ml-2 flex-shrink-0"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)}>
          <div className="w-64 bg-white h-full shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <img 
                  src="/images/lignages-logo-admin.png" 
                  alt="Logo Lignages" 
                  className="w-8 h-8 object-contain"
                />
                <div>
                  <h1 className="text-lg font-bold" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
                    Administration
                  </h1>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Bouton Retour au site - En haut du menu mobile */}
            <div className="px-4 pt-3">
              <a 
                href="/" 
                className="flex items-center space-x-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                style={{ fontFamily: '"Lora", serif' }}
              >
                <Home className="w-4 h-4" style={{ color: 'rgb(196, 150, 96)' }} />
                <span className="text-sm font-medium" style={{ color: 'rgb(196, 150, 96)' }}>
                  Retour au site
                </span>
              </a>
            </div>

            <nav className="p-4 flex-1 overflow-y-auto">
              <div className="space-y-2">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      navigate(`/admin/${item.id}`);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full text-left p-3 rounded-lg transition-all hover:bg-gray-50 ${
                      currentSection === item.id ? 'bg-gray-100' : ''
                    }`}
                    style={{ 
                      fontFamily: '"Lora", serif',
                      borderLeft: currentSection === item.id ? '3px solid rgb(196, 150, 96)' : '3px solid transparent'
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <item.icon 
                        className="w-5 h-5" 
                        style={{ color: currentSection === item.id ? 'rgb(196, 150, 96)' : '#6b7280' }} 
                      />
                      <div>
                        <div 
                          className="font-medium text-sm"
                          style={{ color: currentSection === item.id ? 'rgb(196, 150, 96)' : '#374151' }}
                        >
                          {item.label}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Bouton Espace Membres */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <a 
                  href="/membre" 
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
                  style={{ fontFamily: '"Lora", serif' }}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                  </svg>
                  <span className="text-sm font-medium">Accéder à l'Espace Membres</span>
                </a>
              </div>
            </nav>

            <div className="p-4 border-t">
              <div className="bg-gray-100 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{user.name}</p>
                    <p className="text-xs text-gray-600 truncate">{user.email}</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleLogout}
                    className="hover:bg-gray-200 ml-2 flex-shrink-0"
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 p-4 md:p-8 overflow-x-hidden w-full">
        {currentSection === 'dashboard' && <DashboardContent stats={stats} />}
        {currentSection === 'members' && <MembersManagement />}
        {currentSection === 'database' && <DatabaseManagement />}
        {currentSection === 'cotisations' && <CotisationsManagement />}
        {currentSection === 'site' && <SiteManagement />}
        {currentSection === 'translations' && <TranslationsManagement />}
        {currentSection === 'news' && <NewsManagement />}
        {currentSection === 'newsletter' && <NewsletterManagement />}
        {currentSection === 'publications' && <PublicationsManagement />}
        {currentSection === 'events' && <EventsManagement />}
        {currentSection === 'albums' && <AlbumsManagement />}
        {currentSection === 'familles' && <FamillesBlasonsManagement />}
        {currentSection === 'forum' && <ForumManagement />}
        {currentSection === 'finances' && <FinancesManagement />}
        {currentSection === 'settings' && <SettingsManagement />}
        {currentSection === 'backup' && <BackupManagement />}
      </div>
    </div>
  );
};

// Composant de connexion
const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState('');
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onLogin(email, password);
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMagicLinkError('Veuillez entrer votre email');
      return;
    }

    setIsSendingMagicLink(true);
    setMagicLinkError('');
    setMagicLinkSent(false);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/request-magic-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email,
          frontend_url: window.location.origin  // Envoyer le domaine actuel du frontend
        }),
      });

      if (response.ok) {
        setMagicLinkSent(true);
        setMagicLinkError('');
      } else {
        const data = await response.json();
        setMagicLinkError(data.detail || 'Erreur lors de l\'envoi du lien');
      }
    } catch (error) {
      console.error('Erreur magic link:', error);
      setMagicLinkError('Erreur réseau. Veuillez réessayer.');
    } finally {
      setIsSendingMagicLink(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 relative">
      {/* Bouton Retour au site en haut à gauche */}
      <button
        onClick={() => window.location.href = '/'}
        className="absolute top-6 left-6 flex items-center space-x-2 px-4 py-2 bg-white border-2 rounded-lg transition-all hover:bg-gray-50 shadow-md"
        style={{ 
          borderColor: 'rgb(196, 150, 96)',
          fontFamily: '"Lora", serif'
        }}
      >
        <Home className="w-4 h-4" style={{ color: 'rgb(196, 150, 96)' }} />
        <span className="text-sm font-medium" style={{ color: 'rgb(196, 150, 96)' }}>
          Retour au site
        </span>
      </button>

      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img 
            src="/images/lignages-logo-admin.png" 
            alt="Logo Lignages" 
            className="mx-auto h-20 w-20 object-contain"
          />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900" style={{ fontFamily: '"Lora", serif' }}>
            Administration
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Lignages de Bruxelles
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
              Connexion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ focusRingColor: 'rgb(196, 150, 96)' }}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Mot de passe
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ focusRingColor: 'rgb(196, 150, 96)' }}
                />
              </div>
              <Button 
                type="submit" 
                className="w-full text-white"
                style={{ backgroundColor: 'rgb(196, 150, 96)' }}
              >
                Se connecter
              </Button>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">ou</span>
                </div>
              </div>

              {/* Magic Link Button */}
              <Button
                type="button"
                onClick={handleMagicLink}
                disabled={isSendingMagicLink || !email}
                className="w-full"
                style={{ 
                  backgroundColor: 'white',
                  color: 'rgb(196, 150, 96)',
                  border: '2px solid rgb(196, 150, 96)'
                }}
              >
                {isSendingMagicLink ? 'Envoi en cours...' : '✉️ Connexion par email (sans mot de passe)'}
              </Button>

              {/* Magic Link Success Message */}
              {magicLinkSent && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-800">
                    ✅ Un lien de connexion a été envoyé à <strong>{email}</strong>. 
                    Vérifiez votre boîte email et cliquez sur le lien pour vous connecter.
                  </p>
                </div>
              )}

              {/* Magic Link Error Message */}
              {magicLinkError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">
                    ❌ {magicLinkError}
                  </p>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Tableau de bord principal
const DashboardContent = ({ stats }) => {
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [hiddenActivities, setHiddenActivities] = useState(() => {
    // Charger les activités masquées depuis localStorage
    const saved = localStorage.getItem('hiddenActivities');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/activity`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setActivities(data.activities || []);
      }
    } catch (error) {
      console.error('Erreur chargement activités:', error);
    } finally {
      setLoadingActivities(false);
    }
  };

  // Masquer une activité
  const hideActivity = (activityId) => {
    const newHidden = [...hiddenActivities, activityId];
    setHiddenActivities(newHidden);
    localStorage.setItem('hiddenActivities', JSON.stringify(newHidden));
  };

  // Afficher toutes les activités masquées
  const showAllActivities = () => {
    setHiddenActivities([]);
    localStorage.removeItem('hiddenActivities');
  };

  // Filtrer les activités visibles
  const visibleActivities = activities.filter((activity, index) => {
    const activityId = activity.id || `${activity.title}-${activity.date}-${index}`;
    return !hiddenActivities.includes(activityId);
  });

  const getIconComponent = (iconName) => {
    const icons = {
      'calendar': Calendar,
      'check-circle': UserCheck,
      'message-square': MessageSquare,
    };
    return icons[iconName] || FileText;
  };

  const getColorClass = (color) => {
    const colors = {
      'purple': 'text-purple-500 bg-purple-50',
      'green': 'text-green-500 bg-green-50',
      'blue': 'text-blue-500 bg-blue-50',
      'orange': 'text-orange-500 bg-orange-50',
    };
    return colors[color] || 'text-gray-500 bg-gray-50';
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `il y a ${diffMins} min`;
    if (diffHours < 24) return `il y a ${diffHours}h`;
    if (diffDays < 30) return `il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
          Tableau de bord
        </h1>
        <p className="text-sm sm:text-base text-gray-600">Bienvenue dans l'interface d'administration</p>
      </div>

      {/* Statistiques - Responsive Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        {stats.map((stat, index) => (
          <Card key={index} className="shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-3 sm:p-4 md:p-6">
              <div className="flex flex-col space-y-1 sm:space-y-2">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{stat.title}</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900" style={{ fontFamily: '"Lora", serif' }}>
                  {stat.value}
                </p>
                <p className="text-xs text-gray-500 truncate">{stat.subtitle}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Activités récentes - 30 derniers jours */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg sm:text-xl" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
                Activités récentes (30 derniers jours)
              </CardTitle>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                Réservations, paiements et commentaires forum
              </p>
            </div>
            {hiddenActivities.length > 0 && (
              <button
                onClick={showAllActivities}
                className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                title="Afficher les activités masquées"
              >
                Afficher {hiddenActivities.length} masquée{hiddenActivities.length > 1 ? 's' : ''}
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          {loadingActivities ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: 'rgb(196, 150, 96)' }}></div>
            </div>
          ) : visibleActivities.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-sm sm:text-base">
                {activities.length > 0 ? 'Toutes les activités sont masquées' : 'Aucune activité récente'}
              </p>
              {hiddenActivities.length > 0 && (
                <button
                  onClick={showAllActivities}
                  className="mt-2 text-sm px-3 py-1 rounded transition-colors"
                  style={{ color: 'rgb(196, 150, 96)' }}
                >
                  Tout afficher
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3 max-h-96 sm:max-h-[500px] overflow-y-auto">
              {visibleActivities.map((activity, index) => {
                const IconComponent = getIconComponent(activity.icon);
                const colorClass = getColorClass(activity.color);
                const activityId = activity.id || `${activity.title}-${activity.date}-${index}`;
                
                return (
                  <div 
                    key={activityId} 
                    className="flex items-start space-x-3 sm:space-x-4 p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                  >
                    <div className={`p-2 rounded-full ${colorClass} flex-shrink-0`}>
                      <IconComponent className="w-4 h-4 sm:w-5 sm:h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm sm:text-base text-gray-900 truncate">
                        {activity.title}
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">
                        {activity.description}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatRelativeTime(activity.date)}
                      </p>
                    </div>
                    {/* Bouton masquer */}
                    <button
                      onClick={() => hideActivity(activityId)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition-all"
                      title="Masquer cette activité"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Import des composants de gestion
import MembersManagementComponent from '../components/admin/MembersManagement';
import CotisationManagementComponent from '../components/admin/CotisationManagement';
import DatabaseManagementComponent from '../components/admin/DatabaseManagement';
import NewsManagementComponent from '../components/admin/NewsManagement';
import NewsletterManagementComponent from '../components/admin/NewsletterManagement';
import PublicationsManagementComponent from '../components/admin/PublicationsManagement';
import FamillesBlasonsManagementComponent from '../components/admin/FamillesBlasonsManagement';
import TranslationManagementComponent from '../components/admin/TranslationManagement';
import AlbumsManagementComponent from '../components/admin/AlbumsManagement';

// Composants pour chaque section
const MembersManagement = () => <MembersManagementComponent />;

const DatabaseManagement = () => <DatabaseManagementComponent />;

const CotisationsManagement = () => <CotisationManagementComponent />;

import SiteManagementComponent from '../components/admin/SiteManagement';

const SiteManagement = () => <SiteManagementComponent />;

const TranslationsManagement = () => <TranslationManagementComponent />;

const NewsManagement = () => <NewsManagementComponent />;

const NewsletterManagement = () => <NewsletterManagementComponent />;

const PublicationsManagement = () => <PublicationsManagementComponent />;

const AlbumsManagement = () => <AlbumsManagementComponent />;

import EventsManagementComponent from '../components/admin/EventsManagement';

const EventsManagement = () => <EventsManagementComponent />;

const FamillesBlasonsManagement = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');
  const [view, setView] = useState(tabParam === 'proposals' ? 'proposals' : 'list');
  
  const FamillesBlasonsManagementComponent = require('../components/admin/FamillesBlasonsManagement').default;
  const FamillesProposalsAdmin = require('../components/admin/FamillesProposalsAdmin').default;

  // Mettre à jour la vue quand l'URL change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newTab = params.get('tab');
    if (newTab === 'proposals') {
      setView('proposals');
    } else {
      setView('list');
    }
  }, [location.search]);

  return (
    <div>
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setView('list')}
          className={`px-4 py-2 rounded-lg ${view === 'list' ? 'text-white' : 'bg-gray-100'}`}
          style={view === 'list' ? { backgroundColor: 'rgb(196, 150, 96)' } : {}}
        >
          Familles
        </button>
        <button
          onClick={() => setView('proposals')}
          className={`px-4 py-2 rounded-lg ${view === 'proposals' ? 'text-white' : 'bg-gray-100'}`}
          style={view === 'proposals' ? { backgroundColor: 'rgb(196, 150, 96)' } : {}}
        >
          Propositions membres
        </button>
      </div>
      {view === 'list' ? <FamillesBlasonsManagementComponent /> : <FamillesProposalsAdmin />}
    </div>
  );
};

import ForumManagementComponent from '../components/ForumManagement';
import FinancesManagementComponent from '../components/admin/FinancesManagement';

const ForumManagement = () => {
  const currentUser = localStorage.getItem('currentUser');
  let user = {};
  if (currentUser) {
    try {
      user = JSON.parse(currentUser);
    } catch (e) {
      console.error('Erreur parsing currentUser:', e);
    }
  }
  
  return <ForumManagementComponent 
    userRole={user.role || 'member'} 
    isAdmin={user.role === 'admin'} 
    isModerator={user.is_moderator || false}
    isAdminSpace={true}
  />;
};

const FamillesManagement = () => (
  <div>
    <h1 className="text-3xl font-bold mb-6" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
      Gestion des familles et blasons
    </h1>
    <Card>
      <CardContent className="p-6">
        <p className="text-gray-600">Fonctionnalité en développement...</p>
      </CardContent>
    </Card>
  </div>
);

const FinancesManagement = () => <FinancesManagementComponent />;

const SettingsManagement = () => {
  const [cotisationDefaut, setCotisationDefaut] = useState(100);
  const [isEditing, setIsEditing] = useState(false);
  const [tempAmount, setTempAmount] = useState(100);
  const [loading, setLoading] = useState(false);
  
  // État pour l'email de réception des formulaires
  const [recipientEmail, setRecipientEmail] = useState('lignagesdebruxelles@gmail.com');
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [tempRecipientEmail, setTempRecipientEmail] = useState('');
  
  // États pour SMTP
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: ''
  });
  const [isEditingSmtp, setIsEditingSmtp] = useState(false);
  const [tempSmtpSettings, setTempSmtpSettings] = useState({});
  
  // États pour les clés API
  const [apiKeys, setApiKeys] = useState({
    openai_key: '',
    stripe_key: '',
    stripe_mode: 'test'
  });
  const [isEditingApiKeys, setIsEditingApiKeys] = useState(false);
  const [tempApiKeys, setTempApiKeys] = useState({});

  // États pour Export/Import BD
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dbMessage, setDbMessage] = useState({ type: '', text: '' });

  // États pour la gestion par collections
  const [collections, setCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  
  // États pour l'export GridFS
  const [exportingGridFS, setExportingGridFS] = useState(false);
  const [exportType, setExportType] = useState('');
  
  // États pour Import/Export Unifié
  const [exportingUnified, setExportingUnified] = useState(false);
  const [importingUnified, setImportingUnified] = useState(false);
  
  // États pour les statistiques GridFS
  const [gridfsStats, setGridfsStats] = useState({
    total_files: 0,
    images: 0,
    pdfs: 0,
    total_size_mb: 0
  });

  // Charger les données au démarrage
  useEffect(() => {
    fetchCotisationDefaut();
    fetchRecipientEmail();
    fetchSmtpSettings();
    fetchApiKeys();
  }, []);

  // Charger les statistiques des collections
  useEffect(() => {
    const loadCollections = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/collections-list`);
        if (response.ok) {
          const data = await response.json();
          setCollections(data.collections);
        }
      } catch (error) {
        console.error('Erreur chargement collections:', error);
      }
    };
    loadCollections();
  }, []);
  
  // Charger les statistiques GridFS
  const loadGridFSStats = async (showAlert = false) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      if (!token) {
        if (showAlert) alert('⚠️ Pas de token d\'authentification trouvé');
        console.warn('⚠️ Pas de token pour charger les stats GridFS');
        return;
      }
      
      if (showAlert) alert('🔄 Appel API en cours...');
      console.log('🔄 Chargement stats GridFS...');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gridfs-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Stats GridFS reçues:', data);
        
        // Extraire les stats images et PDFs des catégories
        const newStats = {
          total_files: data.total_files || 0,
          images: data.categories?.images?.count || 0,
          pdfs: data.categories?.pdfs?.count || 0,
          total_size_mb: data.total_size_mb || 0
        };
        
        console.log('📊 Stats formatées:', newStats);
        setGridfsStats(newStats);
        
        if (showAlert) {
          alert(`✅ Stats chargées !\n\nTotal: ${newStats.total_files}\nImages: ${newStats.images}\nPDFs: ${newStats.pdfs}`);
        }
      } else {
        const errorMsg = `❌ Erreur HTTP ${response.status}`;
        console.error(errorMsg);
        if (showAlert) alert(errorMsg);
        if (response.status === 401) {
          console.warn('⚠️ Non authentifié pour les stats GridFS');
        }
      }
    } catch (error) {
      const errorMsg = `❌ Erreur: ${error.message}`;
      console.error(errorMsg, error);
      if (showAlert) alert(errorMsg);
    }
  };
  
  useEffect(() => {
    // Attendre un peu que l'authentification soit établie
    const timer = setTimeout(() => {
      loadGridFSStats();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const fetchCotisationDefaut = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/cotisation-defaut`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCotisationDefaut(data.amount || 100);
        setTempAmount(data.amount || 100);
      }
    } catch (error) {
      console.error('Erreur chargement cotisation défaut:', error);
    }
  };

  const saveCotisationDefaut = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/cotisation-defaut`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: tempAmount
          }),
        }
      );

      if (response.ok) {
        setCotisationDefaut(tempAmount);
        setIsEditing(false);
        alert('Montant de cotisation mis à jour avec succès !');
      } else {
        throw new Error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde du montant');
    } finally {
      setLoading(false);
    }
  };


  // Fonctions Email de réception
  const fetchRecipientEmail = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/email-settings`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRecipientEmail(data.recipient_email || 'lignagesdebruxelles@gmail.com');
        setTempRecipientEmail(data.recipient_email || 'lignagesdebruxelles@gmail.com');
      }
    } catch (error) {
      console.error('Erreur chargement email de réception:', error);
    }
  };

  const saveRecipientEmail = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/email-settings`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            recipient_email: tempRecipientEmail
          }),
        }
      );

      if (response.ok) {
        setRecipientEmail(tempRecipientEmail);
        setIsEditingEmail(false);
        alert('Email de réception mis à jour avec succès !');
      } else {
        throw new Error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur sauvegarde email:', error);
      alert('Erreur lors de la sauvegarde de l\'email');
    } finally {
      setLoading(false);
    }
  };


  // Fonctions SMTP
  const fetchSmtpSettings = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/smtp`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSmtpSettings(data);
        setTempSmtpSettings(data);
      }
    } catch (error) {
      console.error('Erreur chargement SMTP:', error);
    }
  };

  const saveSmtpSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      
      console.log('📤 Envoi paramètres SMTP:', tempSmtpSettings);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/smtp`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(tempSmtpSettings),
        }
      );

      if (response.ok) {
        setSmtpSettings(tempSmtpSettings);
        setIsEditingSmtp(false);
        alert('✅ Paramètres SMTP mis à jour avec succès !');
      } else {
        const errorData = await response.json().catch(() => null);
        console.error('❌ Erreur API SMTP:', response.status, errorData);
        
        // Construire un message d'erreur détaillé
        let errorMessage = `Erreur lors de la sauvegarde des paramètres SMTP\n\n`;
        errorMessage += `Code HTTP: ${response.status} ${response.statusText}\n\n`;
        
        if (errorData) {
          if (errorData.detail) {
            errorMessage += `Détail: ${errorData.detail}\n`;
          }
          if (errorData.message) {
            errorMessage += `Message: ${errorData.message}\n`;
          }
          // Afficher l'objet complet en JSON si pas de détail
          if (!errorData.detail && !errorData.message) {
            errorMessage += `Erreur: ${JSON.stringify(errorData, null, 2)}`;
          }
        } else {
          errorMessage += `Impossible de lire les détails de l'erreur`;
        }
        
        alert(errorMessage);
        throw new Error(errorData?.detail || `HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Erreur sauvegarde SMTP:', error);
      // Si l'erreur n'a pas déjà été affichée
      if (!error.message.includes('HTTP')) {
        alert(`❌ Erreur lors de la sauvegarde des paramètres SMTP:\n\n${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fonctions Clés API
  const fetchApiKeys = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      console.log('🔑 Chargement des clés API...');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/api-keys`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('🔑 Réponse API:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔑 Clés reçues:', data);
        setApiKeys(data);
        setTempApiKeys(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('🔑 Erreur chargement clés API:', response.status, errorData);
      }
    } catch (error) {
      console.error('🔑 Erreur chargement clés API:', error);
    }
  };

  const saveApiKeys = async () => {
    try {
      // Validation côté frontend avant envoi
      const stripeKey = (tempApiKeys.stripe_key || '').trim();
      if (stripeKey && !stripeKey.endsWith('...')) {
        if (stripeKey.startsWith('pk_')) {
          alert('❌ Clé Stripe invalide !\n\nVous avez saisi une clé PUBLIABLE (pk_...).\n\nLe serveur a besoin de la clé SECRÈTE qui commence par "sk_live_" (production) ou "sk_test_" (test).\n\nRécupérez-la sur https://dashboard.stripe.com/apikeys (cliquez sur "Reveal live key").');
          return;
        }
        if (!(stripeKey.startsWith('sk_') || stripeKey.startsWith('rk_'))) {
          alert('❌ Clé Stripe invalide !\n\nLa clé doit commencer par "sk_live_", "sk_test_" ou "rk_". Vérifiez votre dashboard Stripe.');
          return;
        }
        if (tempApiKeys.stripe_mode === 'production' && stripeKey.startsWith('sk_test_')) {
          alert('❌ Incohérence !\n\nMode "Production" sélectionné mais clé de TEST (sk_test_) fournie.\nUtilisez une clé sk_live_ ou repassez en mode Test.');
          return;
        }
        if (tempApiKeys.stripe_mode === 'test' && stripeKey.startsWith('sk_live_')) {
          alert('❌ Incohérence !\n\nMode "Test" sélectionné mais clé LIVE (sk_live_) fournie.\nUtilisez une clé sk_test_ ou passez en mode Production.');
          return;
        }
      }
      
      setLoading(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      
      console.log('📤 Envoi clés API:', tempApiKeys);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/api-keys`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(tempApiKeys),
        }
      );

      if (response.ok) {
        setApiKeys(tempApiKeys);
        setIsEditingApiKeys(false);
        alert('✅ Clés API mises à jour avec succès !');
      } else {
        const errorData = await response.json().catch(() => null);
        console.error('❌ Erreur API clés:', response.status, errorData);
        
        // Construire un message d'erreur détaillé
        let errorMessage = `Erreur lors de la sauvegarde des clés API\n\n`;
        errorMessage += `Code HTTP: ${response.status} ${response.statusText}\n\n`;
        
        if (errorData) {
          if (errorData.detail) {
            errorMessage += `Détail: ${errorData.detail}\n`;
          }
          if (errorData.message) {
            errorMessage += `Message: ${errorData.message}\n`;
          }
          if (!errorData.detail && !errorData.message) {
            errorMessage += `Erreur: ${JSON.stringify(errorData, null, 2)}`;
          }
        } else {
          errorMessage += `Impossible de lire les détails de l'erreur`;
        }
        
        alert(errorMessage);
        throw new Error(errorData?.detail || `HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Erreur sauvegarde clés API:', error);
      // Si l'erreur n'a pas déjà été affichée via alert
      if (!error.message.includes('HTTP')) {
        alert(`❌ Erreur lors de la sauvegarde des clés API:\n\n${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Export collection
  const handleExportCollection = async (collectionName) => {
    try {
      setLoadingCollections(true);
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export-collection/${collectionName}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'export');
      }
      
      const data = await response.json();
      
      // Créer et télécharger le fichier JSON
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${collectionName}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert(`✅ ${data.count} documents exportés`);
    } catch (error) {
      console.error('Erreur export collection:', error);
      alert(`❌ Erreur export: ${error.message}`);
    } finally {
      setLoadingCollections(false);
    }
  };
  
  // Import collection
  const handleImportCollection = async (collectionName, file) => {
    try {
      setLoadingCollections(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/import-collection/${collectionName}`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.imported} documents importés dans ${collectionName}`);
        // Recharger les stats
        const statsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/collections-list`);
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setCollections(statsData.collections);
        }
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.detail}`);
      }
    } catch (error) {
      alert(`Erreur import: ${error.message}`);
    } finally {
      setLoadingCollections(false);
    }
  };
  
  // Reset collection
  const handleResetCollection = async (collectionName) => {
    // Message spécial pour la collection users
    const confirmMessage = collectionName === 'users' 
      ? `⚠️ ATTENTION: Voulez-vous vraiment VIDER la collection "Membres" ?\n\n✅ Les comptes ADMINISTRATEURS seront PRÉSERVÉS\n❌ Tous les autres membres seront SUPPRIMÉS\n\nCette action est IRRÉVERSIBLE !`
      : `⚠️ ATTENTION: Voulez-vous vraiment VIDER la collection "${collectionName}" ?\n\nCette action est IRRÉVERSIBLE !`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      setLoadingCollections(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-collection/${collectionName}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Message spécial pour users avec info sur les admins préservés
        if (collectionName === 'users' && data.preserved_admins) {
          alert(`✅ ${data.deleted} membres supprimés\n\n👤 ${data.preserved_admins} administrateurs préservés`);
        } else {
          alert(`✅ ${data.deleted} documents supprimés de ${collectionName}`);
        }
        
        // Recharger les stats
        const statsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/collections-list`);
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setCollections(statsData.collections);
        }
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.detail}`);
      }
    } catch (error) {
      alert(`Erreur reset: ${error.message}`);
    } finally {
      setLoadingCollections(false);
    }
  };

  // Fonction Export BD
  const handleExportDatabase = async () => {
    // Demander confirmation avec avertissement
    const confirmed = window.confirm(
      '⚠️ Export de la base de données\n\n' +
      'Cette opération peut prendre plusieurs minutes selon la taille de votre base de données.\n\n' +
      'Le site restera accessible pendant l\'export.\n\n' +
      'Voulez-vous continuer ?'
    );
    
    if (!confirmed) {
      return; // Annuler si l'utilisateur refuse
    }
    
    try {
      setExportLoading(true);
      setDbMessage({ type: 'info', text: '⏳ Export en cours... Cette opération peut prendre du temps.' });
      
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/admin/export-database`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lignages_db_export_${new Date().toISOString().split('T')[0]}.tar.gz`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        setDbMessage({ type: 'success', text: '✅ Export réussi! Fichier téléchargé.' });
      } else {
        const error = await response.json();
        setDbMessage({ type: 'error', text: error.detail || '❌ Erreur lors de l\'export' });
      }
    } catch (error) {
      console.error('Erreur export:', error);
      setDbMessage({ type: 'error', text: '❌ Erreur lors de l\'export' });
    } finally {
      setExportLoading(false);
    }
  };

  // Fonction sélection fichier
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ['.tar.gz', '.tgz'];
      const validMimeTypes = ['application/gzip', 'application/x-gzip', 'application/x-tar', 'application/x-compressed-tar'];
      
      const fileName = file.name.toLowerCase();
      const isValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
      const isValidMimeType = validMimeTypes.includes(file.type);
      
      if (isValidExtension || isValidMimeType || file.type === '') {
        setSelectedFile(file);
        setDbMessage({ type: '', text: '' });
      } else {
        setDbMessage({ 
          type: 'error', 
          text: `❌ Fichier non valide. Types acceptés: ${validExtensions.join(', ')}` 
        });
        e.target.value = '';
      }
    }
  };

  // Fonction Import BD
  const handleImportDatabase = async () => {
    if (!selectedFile) {
      setDbMessage({ type: 'error', text: '❌ Veuillez sélectionner un fichier' });
      return;
    }

    if (!window.confirm('⚠️ ATTENTION: Cette opération va REMPLACER toutes les données actuelles! Êtes-vous sûr?')) {
      return;
    }

    try {
      setImportLoading(true);
      setUploadProgress(0);
      setDbMessage({ type: '', text: '' });

      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const chunkSize = 5 * 1024 * 1024; // 5MB chunks
      const totalChunks = Math.ceil(selectedFile.size / chunkSize);
      
      console.log(`📦 Upload de ${totalChunks} chunks (${Math.round(selectedFile.size / (1024 * 1024))} MB total)`);
      
      // Upload par chunks avec retry et vérification
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, selectedFile.size);
        const chunk = selectedFile.slice(start, end);
        
        let uploaded = false;
        let retries = 0;
        const maxRetries = 5; // Augmenté à 5 tentatives
        
        while (!uploaded && retries < maxRetries) {
          try {
            console.log(`📤 Upload chunk ${i + 1}/${totalChunks} (tentative ${retries + 1}/${maxRetries})`);
            
            const formData = new FormData();
            formData.append('file', chunk);
            formData.append('chunk_index', i);
            formData.append('total_chunks', totalChunks);
            formData.append('filename', selectedFile.name);

            const response = await fetch(`${backendUrl}/api/admin/import-database-chunk`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`
              },
              body: formData
            });

            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.detail || 'Erreur lors de l\'upload');
            }
            
            // Vérifier la réponse
            const result = await response.json();
            console.log(`✅ Chunk ${i + 1} reçu par le serveur`);
            
            uploaded = true;
            setUploadProgress(Math.round(((i + 1) / totalChunks) * 100));
            
            // Délai AUGMENTÉ pour éviter la surcharge réseau (500ms au lieu de 100ms)
            if (i < totalChunks - 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          } catch (error) {
            retries++;
            console.error(`❌ Échec chunk ${i + 1}, tentative ${retries}/${maxRetries}:`, error.message);
            
            if (retries >= maxRetries) {
              throw new Error(`Échec upload chunk ${i + 1}/${totalChunks} après ${maxRetries} tentatives: ${error.message}`);
            }
            
            // Attendre PLUS LONGTEMPS avant de réessayer (2 secondes au lieu de 500ms)
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
      }

      console.log('✅ Tous les chunks uploadés, déclenchement de l\'import...');

      // Déclencher l'import final
      const importResponse = await fetch(`${backendUrl}/api/admin/import-database`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          filename: selectedFile.name
        })
      });

      if (importResponse.ok) {
        const result = await importResponse.json();
        setDbMessage({ 
          type: 'success', 
          text: `✅ Import réussi! ${result.collections_imported || 0} collections et ${result.gridfs_files_imported || 0} fichiers importés.` 
        });
        setSelectedFile(null);
      } else {
        const error = await importResponse.json();
        setDbMessage({ type: 'error', text: error.detail || '❌ Erreur lors de l\'import' });
      }
    } catch (error) {
      console.error('Erreur import:', error);
      setDbMessage({ type: 'error', text: error.message || '❌ Erreur lors de l\'import' });
    } finally {
      setImportLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
        Paramètres
      </h1>
      
      <div className="grid gap-6">
        {/* Carte Cotisation par défaut */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span style={{ color: 'rgb(196, 150, 96)' }}>
                💰 Cotisation par défaut
              </span>
              {!isEditing && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsEditing(true)}
                  className="text-gray-600"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-2">
                  Montant que chaque membre annuel devra payer
                </p>
                {isEditing ? (
                  <div className="flex items-center space-x-3">
                    <input
                      type="number"
                      value={tempAmount}
                      onChange={(e) => setTempAmount(parseFloat(e.target.value) || 0)}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(196,150,96)]"
                      min="0"
                      step="1"
                    />
                    <span className="text-lg font-semibold text-gray-700">€</span>
                    
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        onClick={saveCotisationDefaut}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {loading ? 'Sauvegarde...' : 'Sauvegarder'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsEditing(false);
                          setTempAmount(cotisationDefaut);
                        }}
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <span className="text-3xl font-bold" style={{ color: 'rgb(196, 150, 96)' }}>
                      {cotisationDefaut}€
                    </span>
                    <span className="text-gray-500">par membre annuel</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-md">
              <p className="text-sm text-blue-800">
                💡 <strong>Info :</strong> Ce montant sera automatiquement appliqué lors des paiements de cotisations.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Email de réception des formulaires */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span style={{ color: 'rgb(196, 150, 96)' }}>
                📧 Email de réception des formulaires
              </span>
              {!isEditingEmail && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsEditingEmail(true);
                    setTempRecipientEmail(recipientEmail);
                  }}
                  className="text-gray-600"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Cette adresse recevra tous les emails envoyés depuis les formulaires <strong>Contact</strong> et <strong>Devenir Membre</strong>.
              </p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adresse email</label>
                {isEditingEmail ? (
                  <input
                    type="email"
                    value={tempRecipientEmail}
                    onChange={(e) => setTempRecipientEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="exemple@email.com"
                  />
                ) : (
                  <p className="text-gray-900 font-medium">{recipientEmail}</p>
                )}
              </div>

              {isEditingEmail && (
                <div className="flex gap-2">
                  <Button
                    onClick={saveRecipientEmail}
                    disabled={loading}
                    style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                    className="text-white"
                  >
                    {loading ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                  <Button
                    onClick={() => {
                      setIsEditingEmail(false);
                      setTempRecipientEmail(recipientEmail);
                    }}
                    variant="outline"
                  >
                    Annuler
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Paramètres SMTP */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span style={{ color: 'rgb(196, 150, 96)' }}>
                📧 Paramètres SMTP
              </span>
              {!isEditingSmtp && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsEditingSmtp(true);
                    setTempSmtpSettings({...smtpSettings});
                  }}
                  className="text-gray-600"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serveur SMTP</label>
                  {isEditingSmtp ? (
                    <input
                      type="text"
                      value={tempSmtpSettings.smtp_host || ''}
                      onChange={(e) => setTempSmtpSettings({...tempSmtpSettings, smtp_host: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="mail.example.com"
                    />
                  ) : (
                    <p className="text-gray-900">{smtpSettings.smtp_host || 'Non configuré'}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Port SMTP</label>
                  {isEditingSmtp ? (
                    <input
                      type="number"
                      value={tempSmtpSettings.smtp_port || 587}
                      onChange={(e) => setTempSmtpSettings({...tempSmtpSettings, smtp_port: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  ) : (
                    <p className="text-gray-900">{smtpSettings.smtp_port || 587}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom d'utilisateur</label>
                  {isEditingSmtp ? (
                    <input
                      type="text"
                      value={tempSmtpSettings.smtp_username || ''}
                      onChange={(e) => setTempSmtpSettings({...tempSmtpSettings, smtp_username: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="user@example.com"
                    />
                  ) : (
                    <p className="text-gray-900">{smtpSettings.smtp_username || 'Non configuré'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
                  {isEditingSmtp ? (
                    <input
                      type="password"
                      value={tempSmtpSettings.smtp_password || ''}
                      onChange={(e) => setTempSmtpSettings({...tempSmtpSettings, smtp_password: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Mot de passe SMTP"
                    />
                  ) : (
                    <p className="text-gray-900">{smtpSettings.smtp_password ? '***masked***' : 'Non configuré'}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email expéditeur</label>
                  {isEditingSmtp ? (
                    <input
                      type="email"
                      value={tempSmtpSettings.smtp_from_email || ''}
                      onChange={(e) => setTempSmtpSettings({...tempSmtpSettings, smtp_from_email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="noreply@lignagesdebruxelles.be"
                    />
                  ) : (
                    <p className="text-gray-900">{smtpSettings.smtp_from_email || 'Non configuré'}</p>
                  )}
                </div>
              </div>

              {isEditingSmtp && (
                <div className="flex space-x-2 pt-4">
                  <Button
                    size="sm"
                    onClick={saveSmtpSettings}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {loading ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setIsEditingSmtp(false);
                      setTempSmtpSettings({...smtpSettings});
                    }}
                  >
                    Annuler
                  </Button>
                </div>
              )}

              <div className="mt-4 p-3 bg-blue-50 rounded-md">
                <p className="text-sm text-blue-800">
                  💡 <strong>Info :</strong> Ces paramètres sont utilisés pour l'envoi des emails de connexion (magic links) et autres notifications.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clés API */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span style={{ color: 'rgb(196, 150, 96)' }}>
                🔑 Clés API
              </span>
              {!isEditingApiKeys && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setIsEditingApiKeys(true);
                    setTempApiKeys({...apiKeys});
                  }}
                  className="text-gray-600"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Modifier
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* OpenAI */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clé API OpenAI (pour traductions)
                </label>
                {isEditingApiKeys ? (
                  <input
                    type="text"
                    value={tempApiKeys.openai_key || ''}
                    onChange={(e) => setTempApiKeys({...tempApiKeys, openai_key: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="sk-proj-..."
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    {apiKeys.openai_key ? (
                      <>
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                          ✓ Configurée
                        </span>
                        <span className="text-gray-600 font-mono text-sm">{apiKeys.openai_key}</span>
                      </>
                    ) : (
                      <span className="text-gray-500">Non configurée (utilise la clé démo)</span>
                    )}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Si vide, utilise la clé démo. Si renseignée, utilise votre clé OpenAI personnelle.
                </p>
              </div>

              {/* Stripe */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clé API Stripe <span className="text-red-600 font-semibold">SECRÈTE</span> (sk_live_... ou sk_test_...)
                </label>
                {isEditingApiKeys ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={tempApiKeys.stripe_key || ''}
                      onChange={(e) => setTempApiKeys({...tempApiKeys, stripe_key: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md ${
                        tempApiKeys.stripe_key && tempApiKeys.stripe_key.startsWith('pk_')
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300'
                      }`}
                      placeholder="sk_test_... ou sk_live_..."
                      data-testid="stripe-key-input"
                    />
                    {tempApiKeys.stripe_key && tempApiKeys.stripe_key.startsWith('pk_') && (
                      <div className="bg-red-100 border border-red-400 text-red-800 px-3 py-2 rounded text-sm" data-testid="stripe-key-error-publishable">
                        ⚠️ <strong>Erreur :</strong> Vous avez saisi une clé <strong>PUBLIABLE</strong> (pk_...). 
                        Le serveur a besoin de la clé <strong>SECRÈTE</strong> qui commence par <code>sk_live_</code> ou <code>sk_test_</code>.
                        <br />
                        Récupérez-la sur <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-semibold">dashboard.stripe.com/apikeys</a> (cliquez sur "Reveal live key").
                      </div>
                    )}
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="stripe_mode"
                          value="test"
                          checked={tempApiKeys.stripe_mode === 'test'}
                          onChange={(e) => setTempApiKeys({...tempApiKeys, stripe_mode: 'test'})}
                          className="mr-2"
                        />
                        <span className="text-sm">Mode Test</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="stripe_mode"
                          value="production"
                          checked={tempApiKeys.stripe_mode === 'production'}
                          onChange={(e) => setTempApiKeys({...tempApiKeys, stripe_mode: 'production'})}
                          className="mr-2"
                        />
                        <span className="text-sm">Mode Production</span>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center space-x-2">
                      {apiKeys.stripe_key ? (
                        <>
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                            ✓ Configurée
                          </span>
                          <span className="text-gray-600 font-mono text-sm">{apiKeys.stripe_key}</span>
                        </>
                      ) : (
                        <span className="text-gray-500">Non configurée (utilise la clé en mode test)</span>
                      )}
                    </div>
                    {apiKeys.stripe_key && (
                      <p className="text-sm mt-1">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          apiKeys.stripe_mode === 'production' 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {apiKeys.stripe_mode === 'production' ? '🔴 PRODUCTION' : '🧪 TEST'}
                        </span>
                      </p>
                    )}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Si vide, utilise la clé en mode test. Si renseignée, utilise votre clé selon le mode sélectionné.
                </p>
              </div>

              {isEditingApiKeys && (
                <div className="flex space-x-2 pt-4">
                  <Button
                    size="sm"
                    onClick={saveApiKeys}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {loading ? 'Sauvegarde...' : 'Sauvegarder'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setIsEditingApiKeys(false);
                      setTempApiKeys({...apiKeys});
                    }}
                  >
                    Annuler
                  </Button>
                </div>
              )}

              <div className="mt-4 p-3 bg-amber-50 rounded-md border border-amber-200">
                <p className="text-sm text-amber-800">
                  ⚠️ <strong>Attention :</strong> En mode production Stripe, les vrais paiements seront effectués. Assurez-vous de bien tester en mode test avant de passer en production.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Validation RGPD pour tous les membres */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Shield className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
              <span style={{ color: 'rgb(196, 150, 96)' }}>Validation RGPD</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Cette action permet de valider le consentement RGPD pour tous les membres de l'association en une seule fois.
                Utile après un import de données ou pour régulariser les anciens membres.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  ⚠️ <strong>Note :</strong> Cette action marquera tous les utilisateurs comme ayant accepté le RGPD. 
                  Utilisez cette fonction uniquement si vous avez recueilli le consentement de vos membres par un autre moyen.
                </p>
              </div>
              <Button
                onClick={async () => {
                  if (!window.confirm('Êtes-vous sûr de vouloir valider le RGPD pour TOUS les membres ?\n\nCette action est irréversible.')) return;
                  
                  try {
                    const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/validate-rgpd-all`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    
                    if (response.ok) {
                      const data = await response.json();
                      alert(`✅ RGPD validé pour ${data.updated_count} membre(s) !`);
                    } else {
                      const error = await response.json();
                      alert(`❌ Erreur: ${error.detail}`);
                    }
                  } catch (error) {
                    console.error('Erreur validation RGPD:', error);
                    alert('❌ Erreur lors de la validation RGPD');
                  }
                }}
                className="w-full"
                style={{ backgroundColor: 'rgb(196, 150, 96)', color: 'white' }}
              >
                <Shield className="w-4 h-4 mr-2" />
                Valider le RGPD pour tous les membres
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Export/Import de la base de données */}
        <Card className="shadow-sm border border-gray-200">
          <CardHeader className="bg-gray-50">
            <CardTitle className="text-lg flex items-center">
              <Database className="w-5 h-5 mr-2 text-blue-600" />
              Sauvegarde et Restauration de la base de données
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              {/* Message */}
              {dbMessage.text && (
                <div className={`p-4 rounded-lg border-2 ${dbMessage.type === 'success' ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
                  <p className={`font-semibold ${dbMessage.type === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                    {dbMessage.text}
                  </p>
                </div>
              )}

              {/* EXPORT */}
              <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                <h3 className="font-bold text-lg text-blue-900 mb-2 flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Export (Télécharger)
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  Télécharger un backup complet de votre base de données
                </p>
                <ul className="text-sm text-blue-800 space-y-1 mb-4 ml-4">
                  <li>• Toutes les collections (membres, événements, news, familles, blasons...)</li>
                  <li>• Tous les fichiers GridFS (images, PDFs)</li>
                  <li>• Format .tar.gz compressé</li>
                </ul>
                <Button
                  onClick={handleExportDatabase}
                  disabled={exportLoading}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {exportLoading ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Export en cours...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger la sauvegarde
                    </>
                  )}
                </Button>
              </div>

              {/* IMPORT */}
              <div className="border-2 border-green-200 rounded-lg p-4 bg-green-50">
                <h3 className="font-bold text-lg text-green-900 mb-2 flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Import (Restaurer)
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  Restaurer la base de données depuis un fichier de sauvegarde
                </p>
                
                <div className="bg-yellow-100 border-2 border-yellow-500 rounded p-3 mb-4">
                  <p className="text-sm text-yellow-900 font-semibold">
                    ⚠️ ATTENTION: Cette action va REMPLACER toutes les données actuelles!
                  </p>
                </div>

                <div className="space-y-3">
                  <div>
                    <input
                      type="file"
                      id="db-import-file"
                      accept=".tar.gz,.tgz,application/gzip"
                      onChange={handleFileSelect}
                      disabled={importLoading}
                      className="hidden"
                    />
                    <label
                      htmlFor="db-import-file"
                      className={`flex items-center gap-2 px-4 py-3 border-2 border-dashed border-green-400 rounded cursor-pointer hover:bg-green-100 ${importLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Upload className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-900">
                        {selectedFile ? `📄 ${selectedFile.name}` : 'Sélectionner un fichier .tar.gz'}
                      </span>
                    </label>
                  </div>

                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-green-600 h-3 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      >
                        <span className="text-xs text-white font-bold flex items-center justify-center h-full">
                          {uploadProgress}%
                        </span>
                      </div>
                    </div>
                  )}

                  {selectedFile && (
                    <Button
                      onClick={handleImportDatabase}
                      disabled={importLoading}
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      {importLoading ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          Import en cours... {uploadProgress}%
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Restaurer la base de données
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              <div className="p-3 bg-gray-50 rounded-md border border-gray-200">
                <p className="text-xs text-gray-600">
                  💡 <strong>Conseil :</strong> Effectuez des backups réguliers, surtout avant des modifications importantes. Pour synchroniser Preview → Production, exportez depuis Preview et importez en Production.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Gestion par Collections */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Database className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
              <span style={{ color: 'rgb(196, 150, 96)' }}>📦 Gestion par Collections</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Exportez, importez ou réinitialisez des collections individuelles (membres, événements, news, etc.)
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {collections.map((collection) => (
                <div key={collection.name} className="border rounded-lg p-4 space-y-3">
                  <div>
                    <h3 className="font-semibold text-lg">{collection.label}</h3>
                    <p className="text-sm text-gray-600">{collection.count} documents</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => handleExportCollection(collection.name)}
                      disabled={loadingCollections || collection.count === 0}
                      size="sm"
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Download className="w-3 h-3 mr-2" />
                      Exporter JSON
                    </Button>
                    <Button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '.json';
                        input.onchange = (e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImportCollection(collection.name, file);
                        };
                        input.click();
                      }}
                      disabled={loadingCollections}
                      size="sm"
                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Upload className="w-3 h-3 mr-2" />
                      Importer JSON
                    </Button>
                    <Button
                      onClick={() => handleResetCollection(collection.name)}
                      disabled={loadingCollections || collection.count === 0}
                      size="sm"
                      variant="destructive"
                      className="w-full"
                      title={collection.name === 'users' ? 'Supprime tous les membres sauf les administrateurs' : 'Vider complètement cette collection'}
                    >
                      <Trash2 className="w-3 h-3 mr-2" />
                      {collection.name === 'users' ? 'Vider (sauf admins)' : 'Vider'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Gestion des Médias GridFS */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Image className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
              <span style={{ color: 'rgb(196, 150, 96)' }}>📦 Gestion des Médias (GridFS)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Exportez et importez en masse vos fichiers stockés dans GridFS (photos, PDFs, documents)
            </p>
            
            {/* Info importante sur la restauration des liens */}
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>✨ Nouvelle fonctionnalité :</strong> L'export/import préserve automatiquement tous les liens !
              </p>
              <p className="text-xs text-green-700 mt-1">
                🔗 Lors de l'import, les Publications, Albums, News, Events, Users et Blasons retrouvent automatiquement leurs fichiers.
              </p>
            </div>
            
            {/* Statistiques GridFS */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-blue-900">📊 Statistiques de stockage</h3>
                <Button
                  onClick={() => loadGridFSStats(true)}
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-xs"
                  title="Actualiser les statistiques"
                >
                  <RefreshCw className="w-3 h-3" />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Total fichiers</p>
                  <p className="text-lg font-bold text-blue-900">{gridfsStats.total_files.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-600">Images</p>
                  <p className="text-lg font-bold text-green-700">{gridfsStats.images.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-600">PDFs</p>
                  <p className="text-lg font-bold text-red-700">{gridfsStats.pdfs.toLocaleString()}</p>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-2">💾 Taille totale: {gridfsStats.total_size_mb.toFixed(2)} MB</p>
            </div>

            {/* Export/Import par type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Images */}
              <div className="border rounded-lg p-4 space-y-3">
                <div>
                  <h3 className="font-semibold text-lg flex items-center">
                    <Image className="w-5 h-5 mr-2 text-green-600" />
                    Images
                  </h3>
                  <p className="text-sm text-gray-600">{gridfsStats.images} fichiers (JPG, PNG, WebP)</p>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={async () => {
                      try {
                        setExportingGridFS(true);
                        setExportType(`images (${gridfsStats.images} fichiers)`);
                        const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gridfs/export/images`, {
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        });
                        if (response.ok) {
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `images_${new Date().toISOString().split('T')[0]}.zip`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                          alert('✅ Export des images terminé !');
                        } else {
                          alert('❌ Erreur lors de l\'export');
                        }
                      } catch (error) {
                        alert(`❌ Erreur: ${error.message}`);
                      } finally {
                        setExportingGridFS(false);
                        setExportType('');
                      }
                    }}
                    disabled={exportingGridFS}
                    size="sm"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Download className="w-3 h-3 mr-2" />
                    Exporter toutes les images (ZIP)
                  </Button>
                  <Button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = '.zip';
                      input.onchange = async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gridfs/import`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`
                              },
                              body: formData
                            });
                            if (response.ok) {
                              const data = await response.json();
                              let message = `✅ ${data.imported} images importées !`;
                              if (data.links_restored > 0) {
                                message += `\n🔗 ${data.links_restored} liens restaurés automatiquement (albums, users, news, events, blasons)`;
                              }
                              alert(message);
                              loadGridFSStats();
                            } else {
                              const errorData = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
                              alert(`❌ Erreur lors de l'import (${response.status}):\n${errorData.detail || JSON.stringify(errorData)}`);
                            }
                          } catch (error) {
                            alert(`❌ Erreur: ${error.message}`);
                          }
                        }
                      };
                      input.click();
                    }}
                    size="sm"
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Upload className="w-3 h-3 mr-2" />
                    Importer images (ZIP)
                  </Button>
                  <Button
                    onClick={async () => {
                      const confirmation1 = window.confirm('⚠️ DANGER: Supprimer TOUTES les images de GridFS ?\n\nCette action est IRRÉVERSIBLE !\n\nVoulez-vous continuer ?');
                      if (!confirmation1) return;
                      
                      const confirmation2 = window.confirm('⚠️ DERNIÈRE CONFIRMATION\n\nÊtes-vous ABSOLUMENT SÛR de vouloir supprimer les 795 images ?\n\nTapez OK pour confirmer la suppression définitive.');
                      if (!confirmation2) return;
                      
                      try {
                        const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gridfs/clear/images`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        });
                        if (response.ok) {
                          const data = await response.json();
                          alert(`✅ ${data.deleted} images supprimées de GridFS`);
                        } else {
                          alert('❌ Erreur lors de la suppression');
                        }
                      } catch (error) {
                        alert(`❌ Erreur: ${error.message}`);
                      }
                    }}
                    size="sm"
                    variant="destructive"
                    className="w-full"
                  >
                    <Trash2 className="w-3 h-3 mr-2" />
                    Vider toutes les images
                  </Button>
                </div>
              </div>

              {/* PDFs */}
              <div className="border rounded-lg p-4 space-y-3">
                <div>
                  <h3 className="font-semibold text-lg flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-red-600" />
                    Documents PDF
                  </h3>
                  <p className="text-sm text-gray-600">{gridfsStats.pdfs} fichiers (bulletins, publications)</p>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={async () => {
                      try {
                        setExportingGridFS(true);
                        setExportType(`PDFs (${gridfsStats.pdfs} fichiers)`);
                        const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gridfs/export/pdfs`, {
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        });
                        if (response.ok) {
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `pdfs_${new Date().toISOString().split('T')[0]}.zip`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                          alert('✅ Export des PDFs terminé !');
                        } else {
                          alert('❌ Erreur lors de l\'export');
                        }
                      } catch (error) {
                        alert(`❌ Erreur: ${error.message}`);
                      } finally {
                        setExportingGridFS(false);
                        setExportType('');
                      }
                    }}
                    disabled={exportingGridFS}
                    size="sm"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Download className="w-3 h-3 mr-2" />
                    Exporter tous les PDFs (ZIP)
                  </Button>
                  <Button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = '.zip';
                      input.onchange = async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          try {
                            console.log('📤 Import PDFs démarré:', file.name);
                            const formData = new FormData();
                            formData.append('file', file);
                            const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                            console.log('🔑 Token présent:', !!token);
                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gridfs/import`, {
                              method: 'POST',
                              headers: {
                                'Authorization': `Bearer ${token}`
                              },
                              body: formData
                            });
                            console.log('📥 Réponse reçue:', response.status);
                            if (response.ok) {
                              const data = await response.json();
                              let message = `✅ ${data.imported} PDFs importés !`;
                              if (data.links_restored > 0) {
                                message += `\n🔗 ${data.links_restored} liens restaurés automatiquement (publications)`;
                              }
                              alert(message);
                              loadGridFSStats();
                            } else {
                              const errorData = await response.json().catch(() => ({ detail: 'Erreur inconnue' }));
                              alert(`❌ Erreur lors de l'import (${response.status}):\n${errorData.detail || JSON.stringify(errorData)}`);
                            }
                          } catch (error) {
                            alert(`❌ Erreur: ${error.message}`);
                          }
                        }
                      };
                      input.click();
                    }}
                    size="sm"
                    className="w-full bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Upload className="w-3 h-3 mr-2" />
                    Importer PDFs (ZIP)
                  </Button>
                  <Button
                    onClick={async () => {
                      const confirmation1 = window.confirm('⚠️ DANGER: Supprimer TOUS les PDFs de GridFS ?\n\nCette action est IRRÉVERSIBLE !\n\nVoulez-vous continuer ?');
                      if (!confirmation1) return;
                      
                      const confirmation2 = window.confirm('⚠️ DERNIÈRE CONFIRMATION\n\nÊtes-vous ABSOLUMENT SÛR de vouloir supprimer les 265 PDFs ?\n\nTapez OK pour confirmer la suppression définitive.');
                      if (!confirmation2) return;
                      
                      try {
                        const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gridfs/clear/pdfs`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        });
                        if (response.ok) {
                          const data = await response.json();
                          alert(`✅ ${data.deleted} PDFs supprimés de GridFS`);
                        } else {
                          alert('❌ Erreur lors de la suppression');
                        }
                      } catch (error) {
                        alert(`❌ Erreur: ${error.message}`);
                      }
                    }}
                    size="sm"
                    variant="destructive"
                    className="w-full"
                  >
                    <Trash2 className="w-3 h-3 mr-2" />
                    Vider tous les PDFs
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 bg-yellow-50 rounded-md border border-yellow-200">
              <p className="text-xs text-yellow-800">
                ⚠️ <strong>Attention :</strong> L'import de fichiers ne supprime pas les fichiers existants. Les nouveaux fichiers sont ajoutés à GridFS.
                <br />
                💡 <strong>Pour une restauration complète :</strong> Utilisez d'abord le bouton rouge "Vider" pour supprimer tous les fichiers existants, puis importez votre archive ZIP.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Nettoyage automatique des fichiers orphelins */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <RefreshCw className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
              <span style={{ color: 'rgb(196, 150, 96)' }}>🧹 Nettoyage automatique des fichiers orphelins</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Supprimez automatiquement les fichiers qui ne sont plus référencés dans aucune collection (publications, albums, lignages, etc.)
            </p>
            
            <div className="space-y-4">
              {/* Zone d'analyse */}
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h3 className="font-semibold text-orange-900 mb-2 flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  Qu'est-ce qu'un fichier orphelin ?
                </h3>
                <p className="text-xs text-orange-800 mb-2">
                  Un fichier orphelin est un fichier stocké dans GridFS mais qui n'est plus utilisé par aucune publication, album, blason ou actualité. Ces fichiers occupent de l'espace inutilement.
                </p>
                <p className="text-xs text-orange-800">
                  <strong>Exemple :</strong> Un bulletin supprimé dont le PDF reste dans GridFS, ou une photo d'album supprimée qui n'a pas été effacée.
                </p>
              </div>

              {/* Boutons d'action */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gridfs/analyze-orphans`, {
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      });
                      if (response.ok) {
                        const data = await response.json();
                        const percentage = data.percentage_orphans;
                        const message = `📊 ANALYSE DES FICHIERS ORPHELINS\n\n` +
                          `Total fichiers GridFS: ${data.total_files}\n` +
                          `Fichiers utilisés: ${data.referenced_files}\n` +
                          `Fichiers orphelins: ${data.orphan_files} (${percentage}%)\n\n` +
                          `Détail des orphelins:\n` +
                          `  🖼️ Images: ${data.orphan_images}\n` +
                          `  📄 PDFs: ${data.orphan_pdfs}\n` +
                          `  ❓ Autres: ${data.orphan_others}`;
                        alert(message);
                      } else {
                        alert('❌ Erreur lors de l\'analyse');
                      }
                    } catch (error) {
                      alert(`❌ Erreur: ${error.message}`);
                    }
                  }}
                  size="sm"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  🔍 Analyser les fichiers orphelins
                </Button>
                
                <Button
                  onClick={async () => {
                    // Analyse d'abord
                    try {
                      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                      const analyzeResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gridfs/analyze-orphans`, {
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      });
                      if (!analyzeResponse.ok) {
                        alert('❌ Erreur lors de l\'analyse préalable');
                        return;
                      }
                      
                      const analysis = await analyzeResponse.json();
                      
                      if (analysis.orphan_files === 0) {
                        alert('✅ Aucun fichier orphelin détecté !\n\nVotre base de données GridFS est propre.');
                        return;
                      }
                      
                      const confirmation = window.confirm(
                        `🧹 NETTOYAGE DES FICHIERS ORPHELINS\n\n` +
                        `${analysis.orphan_files} fichiers orphelins détectés (${analysis.percentage_orphans}% du stockage)\n\n` +
                        `Détail:\n` +
                        `  🖼️ ${analysis.orphan_images} images\n` +
                        `  📄 ${analysis.orphan_pdfs} PDFs\n` +
                        `  ❓ ${analysis.orphan_others} autres\n\n` +
                        `⚠️ Ces fichiers seront DÉFINITIVEMENT supprimés.\n\n` +
                        `Voulez-vous continuer ?`
                      );
                      
                      if (!confirmation) return;
                      
                      // Nettoyage
                      setExportingGridFS(true);
                      setExportType(`Nettoyage de ${analysis.orphan_files} fichiers orphelins`);
                      
                      const cleanupResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gridfs/cleanup-orphans`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      });
                      
                      if (cleanupResponse.ok) {
                        const result = await cleanupResponse.json();
                        // Rafraîchir les statistiques
                        await loadGridFSStats();
                        alert(
                          `✅ NETTOYAGE TERMINÉ !\n\n` +
                          `${result.deleted} fichiers orphelins supprimés\n` +
                          `${result.errors > 0 ? `⚠️ ${result.errors} erreurs` : ''}\n\n` +
                          `📊 Les statistiques ont été mises à jour.`
                        );
                      } else {
                        alert('❌ Erreur lors du nettoyage');
                      }
                    } catch (error) {
                      alert(`❌ Erreur: ${error.message}`);
                    } finally {
                      setExportingGridFS(false);
                      setExportType('');
                    }
                  }}
                  size="sm"
                  variant="destructive"
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  🧹 Nettoyer maintenant
                </Button>
              </div>

              <div className="p-3 bg-green-50 rounded-md border border-green-200">
                <p className="text-xs text-green-800">
                  💡 <strong>Conseil :</strong> Lancez d'abord une analyse pour voir combien de fichiers orphelins vous avez. Le nettoyage est sans danger car seuls les fichiers non référencés sont supprimés.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Import/Export Intelligent Unifié */}
        <Card className="border-4 border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Zap className="w-5 h-5 mr-2" style={{ color: 'rgb(34, 197, 94)' }} />
              <span style={{ color: 'rgb(34, 197, 94)' }}>⚡ Import/Export Intelligent Unifié</span>
              <span className="ml-2 text-xs bg-green-600 text-white px-2 py-1 rounded-full">NOUVEAU</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Description */}
              <div className="p-4 bg-white border-2 border-green-300 rounded-lg">
                <p className="text-sm font-semibold text-green-900 mb-2">
                  🎯 Solution tout-en-un pour un backup/restauration sûr
                </p>
                <ul className="text-xs text-green-800 space-y-1 ml-4">
                  <li>✅ <strong>Import automatique dans le bon ordre</strong> (GridFS puis Collections)</li>
                  <li>✅ <strong>Reconstruction des liens garantie</strong> (Publications, Albums, Blasons, etc.)</li>
                  <li>✅ <strong>Plus de risque de liens cassés</strong> - Le système gère tout</li>
                  <li>✅ <strong>Includes Pages du site</strong> (absentes de Gestion par Collections)</li>
                </ul>
              </div>

              {/* Boutons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Export Unifié */}
                <div className="border-2 border-green-300 rounded-lg p-4 bg-white space-y-3">
                  <h3 className="font-semibold text-lg text-green-900">📥 Export Unifié</h3>
                  <p className="text-xs text-gray-600">
                    Exporte TOUT en un seul ZIP : Collections + GridFS avec métadonnées de liaison
                  </p>
                  <Button
                    onClick={async () => {
                      try {
                        setExportingUnified(true);
                        const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/unified-export`, {
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        });
                        
                        if (response.ok) {
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `unified_export_${new Date().toISOString().split('T')[0]}.zip`;
                          a.click();
                          alert('✅ Export Unifié téléchargé ! Contient Collections + GridFS avec liens préservés.');
                        } else {
                          alert('❌ Erreur lors de l\'export unifié');
                        }
                      } catch (error) {
                        alert(`❌ Erreur: ${error.message}`);
                      } finally {
                        setExportingUnified(false);
                      }
                    }}
                    disabled={exportingUnified}
                    className="w-full bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-semibold"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {exportingUnified ? 'Export en cours...' : '📥 Exporter TOUT (Unifié)'}
                  </Button>
                </div>

                {/* Import Unifié */}
                <div className="border-2 border-green-300 rounded-lg p-4 bg-white space-y-3">
                  <h3 className="font-semibold text-lg text-green-900">📤 Import Unifié</h3>
                  <p className="text-xs text-gray-600">
                    Sélectionnez TOUS vos fichiers (JSON + ZIP). L'ordre n'a pas d'importance !
                  </p>
                  <Button
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = '.json,.zip,.tar.gz,.gz';
                      input.multiple = true;
                      input.onchange = async (e) => {
                        const files = Array.from(e.target.files || []);
                        if (files.length === 0) return;
                        
                        const confirmation = window.confirm(
                          `⚠️ ATTENTION : Import Intelligent Unifié\n\n` +
                          `Vous allez importer ${files.length} fichier(s) :\n` +
                          files.map(f => `  - ${f.name}`).join('\n') + `\n\n` +
                          `Le système va :\n` +
                          `1. Analyser et trier les fichiers\n` +
                          `2. Importer GridFS d'abord (nouveaux ObjectIds)\n` +
                          `3. Importer Collections ensuite (liens reconstruits)\n\n` +
                          `Voulez-vous continuer ?`
                        );
                        
                        if (!confirmation) return;
                        
                        try {
                          setImportingUnified(true);
                          const formData = new FormData();
                          files.forEach(file => formData.append('files', file));
                          
                          const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
                          
                          // Timeout de 10 minutes pour les gros fichiers
                          const controller = new AbortController();
                          const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 minutes
                          
                          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/unified-import`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${token}`
                            },
                            body: formData,
                            signal: controller.signal
                          });
                          
                          clearTimeout(timeoutId);
                          
                          if (response.ok) {
                            const data = await response.json();
                            alert(
                              `✅ ${data.message}\n\n` +
                              `📦 GridFS:\n` +
                              `  - ${data.gridfs.files_imported} fichiers importés\n` +
                              `  - ${data.gridfs.mappings_created} correspondances créées\n\n` +
                              `📚 Collections:\n` +
                              `  - ${data.collections.documents_imported} documents importés\n` +
                              `  - ${data.collections.links_updated} liens reconstruits\n\n` +
                              `Collections mises à jour:\n` +
                              Object.entries(data.collections.collections).map(([name, count]) => `  - ${name}: ${count}`).join('\n')
                            );
                            
                            // Rafraîchir les stats
                            loadGridFSStats();
                          } else {
                            const errorData = await response.json();
                            alert(`❌ Erreur: ${errorData.detail || 'Import échoué'}`);
                          }
                        } catch (error) {
                          if (error.name === 'AbortError') {
                            alert(`❌ Timeout: L'import a pris trop de temps (>10 minutes).\n\nPour les très gros fichiers, veuillez :\n1. Utiliser l'export .tar.gz au lieu de l'Export Unifié\n2. Ou contacter le support pour une migration assistée`);
                          } else {
                            alert(`❌ Erreur d'import: ${error.message}\n\nSi le fichier est très volumineux (>100 MB), essayez l'export .tar.gz qui est optimisé pour les gros volumes.`);
                          }
                        } finally {
                          setImportingUnified(false);
                        }
                      };
                      input.click();
                    }}
                    disabled={importingUnified}
                    className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white font-semibold"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {importingUnified ? 'Import en cours...' : '📤 Importer (Unifié)'}
                  </Button>
                </div>
              </div>

              {/* Aide */}
              <div className="p-3 bg-yellow-50 rounded-md border border-yellow-200">
                <p className="text-xs text-yellow-900">
                  💡 <strong>Conseil :</strong> L'Import Unifié est LA méthode recommandée pour migrer Preview → Production ou pour restaurer un backup. Il garantit que tous les liens (Publications, Albums, Blasons, Pages) fonctionnent correctement après l'import.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Modal de chargement export GridFS */}
      {exportingGridFS && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="mb-4">
                <RefreshCw className="w-16 h-16 mx-auto text-blue-600 animate-spin" />
              </div>
              <h3 className="text-xl font-bold mb-2">Génération de l'archive en cours...</h3>
              <p className="text-gray-600 mb-4">
                Export de <strong>{exportType}</strong>
              </p>
              <p className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded-md border border-yellow-200">
                ⏳ <strong>Veuillez patienter sans quitter cette page</strong>
                <br />
                Cette opération peut prendre jusqu'à 30 secondes
              </p>
              <div className="mt-4 flex justify-center">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de chargement Export Unifié */}
      {exportingUnified && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="mb-4">
                <RefreshCw className="w-16 h-16 mx-auto text-green-600 animate-spin" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-green-900">⚡ Export Intelligent Unifié</h3>
              <p className="text-gray-600 mb-4">
                Création de l'archive complète en cours...
              </p>
              <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md border border-gray-200 mb-4">
                <p className="font-semibold mb-2">📦 Export en cours :</p>
                <ul className="text-left space-y-1 ml-4">
                  <li>• Collections (users, events, albums, etc.)</li>
                  <li>• GridFS (186 fichiers : images + PDFs)</li>
                  <li>• Métadonnées de liaison</li>
                </ul>
              </div>
              <p className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded-md border border-yellow-200">
                ⏳ <strong>Veuillez patienter sans quitter cette page</strong>
                <br />
                Cette opération peut prendre 1-2 minutes
              </p>
              <div className="mt-4 flex justify-center">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-green-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-green-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-green-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de chargement Import Unifié */}
      {importingUnified && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="mb-4">
                <RefreshCw className="w-16 h-16 mx-auto text-blue-600 animate-spin" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-blue-900">⚡ Import Intelligent Unifié</h3>
              <p className="text-gray-600 mb-4">
                Restauration de la base de données en cours...
              </p>
              <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md border border-gray-200 mb-4">
                <p className="font-semibold mb-2">🔄 Opérations en cours :</p>
                <ul className="text-left space-y-1 ml-4">
                  <li>• Analyse des fichiers</li>
                  <li>• Import GridFS (prioritaire)</li>
                  <li>• Import Collections</li>
                  <li>• Reconstruction des liens</li>
                </ul>
              </div>
              <p className="text-sm text-red-700 bg-red-50 p-3 rounded-md border border-red-200">
                ⚠️ <strong>Ne fermez PAS cette page !</strong>
                <br />
                L'import peut prendre 2-5 minutes selon la taille
              </p>
              <div className="mt-4 flex justify-center">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== BACKUP MANAGEMENT ====================
const BackupManagement = () => {
  const [exportingDB, setExportingDB] = useState(false);
  const [exportingFiles, setExportingFiles] = useState(false);
  const [exportingBlasons, setExportingBlasons] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);
  const [importingDB, setImportingDB] = useState(false);
  const [importingFiles, setImportingFiles] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, status: '' });
  const [backupStats, setBackupStats] = useState(null);

  // Migration VPS (sauvegarde complète pour déménagement)
  const [migrationJobId, setMigrationJobId] = useState(null);
  const [migrationStatus, setMigrationStatus] = useState(null); // 'pending'|'running'|'ready'|'failed'
  const [migrationProgress, setMigrationProgress] = useState('');
  const [migrationInfo, setMigrationInfo] = useState(null);
  const [migrationPolling, setMigrationPolling] = useState(false);

  useEffect(() => {
    loadBackupStats();
  }, []);

  // Polling automatique du job de migration
  useEffect(() => {
    if (!migrationJobId || !migrationPolling) return;
    const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
    const tick = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/full-migration/status/${migrationJobId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!r.ok) return;
        const d = await r.json();
        setMigrationStatus(d.status);
        setMigrationProgress(d.progress || '');
        if (d.status === 'ready' || d.status === 'failed') {
          setMigrationInfo(d);
          setMigrationPolling(false);
        }
      } catch (e) { /* silent */ }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => clearInterval(id);
  }, [migrationJobId, migrationPolling]);

  const startFullMigration = async () => {
    if (!window.confirm('Lancer la sauvegarde complète (mongodump + fichiers + docker-compose) ?\n\nCela peut prendre plusieurs minutes selon le volume de données.')) return;
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/full-migration/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!r.ok) throw new Error('Impossible de démarrer la sauvegarde');
      const d = await r.json();
      setMigrationJobId(d.job_id);
      setMigrationStatus('pending');
      setMigrationProgress('Démarrage...');
      setMigrationInfo(null);
      setMigrationPolling(true);
    } catch (e) {
      alert('Erreur : ' + e.message);
    }
  };

  const downloadMigrationArchive = async () => {
    if (!migrationJobId || migrationStatus !== 'ready') return;
    const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/full-migration/download/${migrationJobId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const sha = r.headers.get('X-Content-SHA256');
      const filename = (r.headers.get('Content-Disposition') || '').match(/filename="?([^";]+)"?/)?.[1] || 'lignages-migration.tar.gz';
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
      if (sha) {
        setTimeout(() => alert(`Téléchargement terminé.\n\nSHA-256 de l'archive (à vérifier après download) :\n${sha}\n\nSur Linux/macOS :\n  sha256sum ${filename}\n\nSur Windows PowerShell :\n  Get-FileHash ${filename} -Algorithm SHA256`), 300);
      }
    } catch (e) {
      alert('Erreur téléchargement : ' + e.message);
    }
  };

  const deleteMigrationJob = async () => {
    if (!migrationJobId) return;
    if (!window.confirm('Supprimer cette sauvegarde du serveur ?')) return;
    const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/full-migration/${migrationJobId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMigrationJobId(null);
      setMigrationStatus(null);
      setMigrationInfo(null);
      setMigrationProgress('');
    } catch (e) { /* silent */ }
  };

  const [apiList, setApiList] = useState([]);

  const loadBackupStats = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBackupStats(data);
      }
      
      // Charger aussi la liste des API
      const apiResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/api-list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (apiResponse.ok) {
        const apiData = await apiResponse.json();
        setApiList(apiData.apis || []);
      }
    } catch (error) {
      console.error('Erreur chargement stats backup:', error);
    }
  };

  // Export CSV des familles et blasons
  const exportFamillesCSV = async () => {
    setExportingCSV(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/export-familles-csv`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const famillesCount = response.headers.get('X-Familles-Count') || '?';
        const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `familles_blasons_${date}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
        alert(`✅ ${famillesCount} familles exportées en CSV avec succès !`);
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur export CSV:', error);
      alert(`❌ Erreur: ${error.message}`);
    } finally {
      setExportingCSV(false);
    }
  };

  // Export des images de blasons
  const exportBlasonsImages = async () => {
    setExportingBlasons(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/export-blasons-images`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const imagesCount = response.headers.get('X-Images-Count') || '?';
        const date = new Date().toISOString().split('T')[0].replace(/-/g, '');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `blasons_images_${date}.zip`;
        a.click();
        URL.revokeObjectURL(a.href);
        alert(`✅ ${imagesCount} images de blasons exportées avec succès !`);
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur export blasons:', error);
      alert(`❌ Erreur: ${error.message}`);
    } finally {
      setExportingBlasons(false);
    }
  };

  const downloadCollection = async (collectionName) => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/collection/${collectionName}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${collectionName}_export_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.detail}`);
      }
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      alert('Erreur lors du téléchargement');
    }
  };

  const copyApiUrl = (endpoint) => {
    const fullUrl = `${window.location.origin}${endpoint}`;
    navigator.clipboard.writeText(fullUrl);
    alert(`URL copiée: ${fullUrl}`);
  };

  const exportDatabase = async () => {
    if (!window.confirm('Exporter TOUTE la base de données en JSON ?\n\nCela peut prendre plusieurs minutes.')) return;
    
    setExportingDB(true);
    setProgress({ current: 0, total: 100, status: 'Préparation de l\'export...' });
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/export-database`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const date = new Date().toISOString().split('T')[0];
        a.download = `lignages_database_backup_${date}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        alert('✅ Base de données exportée avec succès !');
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.detail}`);
      }
    } catch (error) {
      console.error('Erreur export DB:', error);
      alert('❌ Erreur lors de l\'export');
    } finally {
      setExportingDB(false);
      setProgress({ current: 0, total: 0, status: '' });
    }
  };

  const exportFiles = async (partNumber = null) => {
    const totalParts = backupStats?.file_parts || 1;
    
    if (partNumber === null) {
      // Export de toutes les parties une par une
      if (!window.confirm(`Exporter les fichiers en ${totalParts} partie(s) ?\n\nChaque partie sera téléchargée séparément.`)) return;
      
      setExportingFiles(true);
      
      for (let i = 0; i < totalParts; i++) {
        setProgress({ current: i + 1, total: totalParts, status: `Téléchargement partie ${i + 1}/${totalParts}...` });
        
        try {
          const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/export-files?part=${i}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const date = new Date().toISOString().split('T')[0];
            a.download = `lignages_files_part${i + 1}_of_${totalParts}_${date}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
            
            // Attendre un peu entre chaque téléchargement
            if (i < totalParts - 1) {
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          } else {
            const error = await response.json();
            alert(`❌ Erreur partie ${i + 1}: ${error.detail}`);
            break;
          }
        } catch (error) {
          console.error(`Erreur export partie ${i + 1}:`, error);
          alert(`❌ Erreur lors de l'export de la partie ${i + 1}`);
          break;
        }
      }
      
      setExportingFiles(false);
      setProgress({ current: 0, total: 0, status: '' });
      alert(`✅ Export terminé ! ${totalParts} fichier(s) ZIP téléchargé(s).`);
      
    } else {
      // Export d'une seule partie
      setExportingFiles(true);
      setProgress({ current: partNumber + 1, total: totalParts, status: `Téléchargement partie ${partNumber + 1}...` });
      
      try {
        const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/export-files?part=${partNumber}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const date = new Date().toISOString().split('T')[0];
          a.download = `lignages_files_part${partNumber + 1}_of_${totalParts}_${date}.zip`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          a.remove();
          alert(`✅ Partie ${partNumber + 1} exportée avec succès !`);
        } else {
          const error = await response.json();
          alert(`❌ Erreur: ${error.detail}`);
        }
      } catch (error) {
        console.error('Erreur export fichiers:', error);
        alert('❌ Erreur lors de l\'export');
      } finally {
        setExportingFiles(false);
        setProgress({ current: 0, total: 0, status: '' });
      }
    }
  };

  const importDatabase = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!window.confirm(`Importer la base de données depuis "${file.name}" ?\n\n⚠️ ATTENTION: Cela REMPLACERA toutes les données actuelles !`)) {
      event.target.value = '';
      return;
    }
    
    if (!window.confirm('Êtes-vous VRAIMENT sûr ? Cette action est IRRÉVERSIBLE !')) {
      event.target.value = '';
      return;
    }
    
    setImportingDB(true);
    setProgress({ current: 0, total: 100, status: 'Import en cours...' });
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/import-database`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✅ Base de données importée avec succès !\n\n${data.message}`);
        loadBackupStats();
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.detail}`);
      }
    } catch (error) {
      console.error('Erreur import DB:', error);
      alert('❌ Erreur lors de l\'import');
    } finally {
      setImportingDB(false);
      setProgress({ current: 0, total: 0, status: '' });
      event.target.value = '';
    }
  };

  const importFiles = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!window.confirm(`Importer les fichiers depuis "${file.name}" ?\n\nLes fichiers existants avec le même ID seront remplacés.`)) {
      event.target.value = '';
      return;
    }
    
    setImportingFiles(true);
    setProgress({ current: 0, total: 100, status: 'Import des fichiers en cours...' });
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup/import-files`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        alert(`✅ Fichiers importés avec succès !\n\n${data.message}`);
        loadBackupStats();
      } else {
        const error = await response.json();
        alert(`❌ Erreur: ${error.detail}`);
      }
    } catch (error) {
      console.error('Erreur import fichiers:', error);
      alert('❌ Erreur lors de l\'import des fichiers');
    } finally {
      setImportingFiles(false);
      setProgress({ current: 0, total: 0, status: '' });
      event.target.value = '';
    }
  };

  const isLoading = exportingDB || exportingFiles || importingDB || importingFiles;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
            Sauvegarde Complète
          </h2>
          <p className="text-gray-600 mt-1">
            Exportez et importez toutes les données du site pour une sauvegarde ou migration complète
          </p>
        </div>
      </div>

      {/* ============================================================ */}
      {/* Migration VPS — Sauvegarde complète (mongodump + fichiers)   */}
      {/* ============================================================ */}
      <Card className="border-2 border-orange-300 bg-orange-50">
        <CardHeader>
          <CardTitle className="flex items-center text-orange-900">
            <Download className="w-5 h-5 mr-2" />
            Sauvegarde complète pour migration VPS
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-orange-900 bg-white/60 rounded p-3 border border-orange-200">
            <strong>Contenu de l'archive .tar.gz générée :</strong>
            <ul className="list-disc ml-5 mt-2 space-y-1">
              <li><code>mongodump/</code> — dump BSON complet de <code>lignages_db</code> (34 collections, index inclus)</li>
              <li><code>gridfs_files/</code> — copie plate de tous les fichiers GridFS (photos, blasons, PDFs)</li>
              <li><code>deploy/</code> — <code>docker-compose.yml</code>, <code>Dockerfile.backend/frontend</code>, <code>nginx.conf</code>, <code>restore.sh</code>, <code>env.template</code>, <code>hestia-proxy.conf.sample</code></li>
              <li><code>INVENTORY.json</code> + <code>SHA256SUMS.txt</code> + <code>README.md</code></li>
            </ul>
            <p className="mt-2 text-xs">
              💡 <strong>Pour une sauvegarde cohérente</strong> : idéalement pas de trafic pendant l'export. En cas d'écriture pendant l'export, seule la dernière collection dumpée peut être affectée — pas de perte, juste un petit décalage possible.
            </p>
          </div>

          {!migrationJobId && (
            <Button
              onClick={startFullMigration}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="start-full-migration-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Générer la sauvegarde complète
            </Button>
          )}

          {migrationJobId && migrationStatus !== 'ready' && migrationStatus !== 'failed' && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600"></div>
                <span className="text-sm text-orange-900">{migrationProgress || 'Génération en cours...'}</span>
              </div>
              <p className="text-xs text-gray-600">Job ID : <code>{migrationJobId.slice(0, 12)}...</code></p>
            </div>
          )}

          {migrationStatus === 'failed' && migrationInfo && (
            <div className="bg-red-100 border border-red-400 text-red-800 p-3 rounded text-sm">
              <strong>Échec de la sauvegarde :</strong><br />
              {migrationInfo.error || 'Erreur inconnue'}
              <div className="mt-2">
                <Button size="sm" variant="outline" onClick={deleteMigrationJob}>Fermer</Button>
              </div>
            </div>
          )}

          {migrationStatus === 'ready' && migrationInfo && (
            <div className="bg-green-100 border border-green-400 p-4 rounded space-y-3">
              <div className="text-sm text-green-900">
                <strong>✅ Sauvegarde prête au téléchargement</strong>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="font-semibold">Fichier :</span> {migrationInfo.archive_filename}</div>
                <div><span className="font-semibold">Taille :</span> {(migrationInfo.archive_size / 1024 / 1024).toFixed(2)} Mo</div>
                <div><span className="font-semibold">Collections :</span> {migrationInfo.inventory?.collections_count}</div>
                <div><span className="font-semibold">Documents :</span> {migrationInfo.inventory?.total_documents}</div>
                <div className="col-span-2"><span className="font-semibold">GridFS :</span> {migrationInfo.inventory?.gridfs_extracted_files} fichiers ({((migrationInfo.inventory?.gridfs_extracted_bytes || 0) / 1024 / 1024).toFixed(1)} Mo)</div>
                <div className="col-span-2 break-all"><span className="font-semibold">SHA-256 :</span> <code className="text-[10px]">{migrationInfo.sha256}</code></div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={downloadMigrationArchive}
                  className="bg-green-700 hover:bg-green-800 text-white"
                  data-testid="download-migration-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger l'archive
                </Button>
                <Button
                  onClick={deleteMigrationJob}
                  variant="outline"
                  data-testid="delete-migration-btn"
                >
                  Supprimer du serveur
                </Button>
              </div>
              <p className="text-[11px] text-gray-600">
                💾 L'archive reste stockée temporairement sur le serveur jusqu'à suppression manuelle ou redémarrage. Téléchargez-la puis supprimez-la.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistiques */}
      {backupStats && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Database className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
              <span style={{ color: 'rgb(196, 150, 96)' }}>État actuel de la base de données</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{backupStats.total_collections}</div>
                <div className="text-sm text-blue-800">Collections</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{backupStats.total_documents?.toLocaleString()}</div>
                <div className="text-sm text-green-800">Documents</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{backupStats.total_files}</div>
                <div className="text-sm text-purple-800">Fichiers</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">{backupStats.total_size_mb?.toFixed(1)} MB</div>
                <div className="text-sm text-orange-800">Taille totale</div>
              </div>
            </div>
            
            {/* Détail des collections */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-700 mb-2">Collections incluses dans le backup :</h4>
              <div className="flex flex-wrap gap-2">
                {backupStats.collections?.map((col, idx) => (
                  <span key={idx} className="px-2 py-1 bg-white rounded border text-xs text-gray-600">
                    {col.name} ({col.count})
                  </span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Export Base de données */}
        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center text-blue-800">
              <Download className="w-5 h-5 mr-2" />
              Export Base de Données
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-600 mb-4">
              Exporte <strong>TOUTES</strong> les collections MongoDB en un seul fichier JSON :
            </p>
            <ul className="text-sm text-gray-500 mb-4 list-disc list-inside space-y-1">
              <li>Utilisateurs et membres</li>
              <li>Cotisations et paiements</li>
              <li>Événements et inscriptions</li>
              <li>Albums photos</li>
              <li>Familles et blasons</li>
              <li>Pages du site et paramètres</li>
              <li>Publications et news</li>
              <li>Et toutes les autres données...</li>
            </ul>
            <Button
              onClick={exportDatabase}
              disabled={isLoading}
              className="w-full"
              style={{ backgroundColor: 'rgb(59, 130, 246)', color: 'white' }}
            >
              {exportingDB ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Export en cours...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Exporter la base de données (JSON)
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Export Fichiers */}
        <Card className="border-2 border-purple-200">
          <CardHeader className="bg-purple-50">
            <CardTitle className="flex items-center text-purple-800">
              <Download className="w-5 h-5 mr-2" />
              Export Fichiers ({backupStats?.file_parts || 0} parties)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-gray-600 mb-4">
              Exporte <strong>TOUS</strong> les fichiers en plusieurs parties de 50 fichiers chacune :
            </p>
            <ul className="text-sm text-gray-500 mb-4 list-disc list-inside space-y-1">
              <li>Images (photos de profil, albums, blasons)</li>
              <li>Documents PDF</li>
              <li>Images des pages du site</li>
              <li>Pièces jointes</li>
            </ul>
            
            {backupStats?.file_parts > 0 && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-purple-800">
                  📦 <strong>{backupStats.total_files} fichiers</strong> seront exportés en <strong>{backupStats.file_parts} partie(s)</strong> de {backupStats.files_per_part} fichiers max.
                </p>
              </div>
            )}
            
            <Button
              onClick={() => exportFiles()}
              disabled={isLoading}
              className="w-full mb-3"
              style={{ backgroundColor: 'rgb(147, 51, 234)', color: 'white' }}
            >
              {exportingFiles ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {progress.status || 'Export en cours...'}
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Exporter toutes les parties ({backupStats?.file_parts || 0} ZIP)
                </>
              )}
            </Button>
            
            {/* Boutons individuels pour chaque partie */}
            {backupStats?.file_parts > 1 && (
              <div className="mt-4">
                <p className="text-xs text-gray-500 mb-2">Ou télécharger une partie spécifique :</p>
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: backupStats.file_parts }, (_, i) => (
                    <Button
                      key={i}
                      onClick={() => exportFiles(i)}
                      disabled={isLoading}
                      variant="outline"
                      size="sm"
                      className="text-xs"
                    >
                      Partie {i + 1}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Export Images Blasons - Spécial Familles et Blasons */}
      <Card className="border-2 border-amber-200 mt-6">
        <CardHeader className="bg-amber-50">
          <CardTitle className="flex items-center text-amber-800">
            <Shield className="w-5 h-5 mr-2" />
            Export Familles et Blasons
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-gray-600 mb-4">
            Exportez les données de la base "Familles et Blasons" en différents formats.
          </p>
          
          {/* Export CSV */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2 flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Export CSV (Données)
            </h4>
            <ul className="text-sm text-gray-600 mb-3 list-disc list-inside space-y-1">
              <li>Toutes les familles avec leurs informations</li>
              <li>Descriptions des blasons</li>
              <li>Noms des fichiers images</li>
              <li>Compatible Excel et LibreOffice</li>
            </ul>
            <Button
              onClick={exportFamillesCSV}
              disabled={exportingCSV}
              className="w-full"
              style={{ backgroundColor: 'rgb(37, 99, 235)', color: 'white' }}
            >
              {exportingCSV ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Export en cours...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Exporter en CSV (données)
                </>
              )}
            </Button>
          </div>
          
          {/* Export Images ZIP */}
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-800 mb-2 flex items-center">
              <Image className="w-4 h-4 mr-2" />
              Export ZIP (Images)
            </h4>
            <ul className="text-sm text-gray-600 mb-3 list-disc list-inside space-y-1">
              <li>Toutes les images de blasons</li>
              <li>Nommées par nom de famille</li>
              <li>Format original (PNG, JPG, etc.)</li>
            </ul>
            <Button
              onClick={exportBlasonsImages}
              disabled={exportingBlasons}
              className="w-full"
              style={{ backgroundColor: 'rgb(217, 119, 6)', color: 'white' }}
            >
              {exportingBlasons ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Export en cours...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Exporter les images (ZIP)
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Import */}
      <div className="grid md:grid-cols-2 gap-6 mt-6">
        {/* Import Base de données */}
        <Card className="border-2 border-green-200">
          <CardHeader className="bg-green-50">
            <CardTitle className="flex items-center text-green-800">
              <Upload className="w-5 h-5 mr-2" />
              Import Base de Données
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-800">
                ⚠️ <strong>ATTENTION :</strong> L'import REMPLACERA toutes les données actuelles. 
                Assurez-vous d'avoir une sauvegarde avant de procéder.
              </p>
            </div>
            <p className="text-gray-600 mb-4">
              Restaurez la base de données depuis un fichier JSON exporté précédemment.
            </p>
            <label className="block">
              <input
                type="file"
                accept=".json"
                onChange={importDatabase}
                disabled={isLoading}
                className="hidden"
              />
              <Button
                as="span"
                disabled={isLoading}
                className="w-full cursor-pointer"
                style={{ backgroundColor: 'rgb(34, 197, 94)', color: 'white' }}
                onClick={(e) => e.target.closest('label').querySelector('input').click()}
              >
                {importingDB ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Importer la base de données (JSON)
                  </>
                )}
              </Button>
            </label>
          </CardContent>
        </Card>

        {/* Import Fichiers */}
        <Card className="border-2 border-orange-200">
          <CardHeader className="bg-orange-50">
            <CardTitle className="flex items-center text-orange-800">
              <Upload className="w-5 h-5 mr-2" />
              Import Fichiers
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                ℹ️ Les fichiers existants avec le même ID seront remplacés. Les nouveaux fichiers seront ajoutés.
              </p>
            </div>
            <p className="text-gray-600 mb-4">
              Restaurez les fichiers depuis un ZIP exporté précédemment.
            </p>
            <label className="block">
              <input
                type="file"
                accept=".zip"
                onChange={importFiles}
                disabled={isLoading}
                className="hidden"
              />
              <Button
                as="span"
                disabled={isLoading}
                className="w-full cursor-pointer"
                style={{ backgroundColor: 'rgb(249, 115, 22)', color: 'white' }}
                onClick={(e) => e.target.closest('label').querySelector('input').click()}
              >
                {importingFiles ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Import en cours...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Importer les fichiers (ZIP)
                  </>
                )}
              </Button>
            </label>
          </CardContent>
        </Card>
      </div>

      {/* Section API - Export par collection */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Code className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
            <span style={{ color: 'rgb(196, 150, 96)' }}>API d'Export par Collection</span>
          </CardTitle>
          <p className="text-sm text-gray-500 mt-1">
            Téléchargez chaque collection individuellement en JSON. Cliquez sur "Télécharger" ou copiez l'URL de l'API.
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left p-3 font-medium">Collection</th>
                  <th className="text-left p-3 font-medium">Description</th>
                  <th className="text-center p-3 font-medium">Documents</th>
                  <th className="text-left p-3 font-medium">Endpoint API</th>
                  <th className="text-center p-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiList.map((api, idx) => (
                  <tr key={api.collection} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="p-3 font-medium text-gray-900">{api.name}</td>
                    <td className="p-3 text-gray-600 text-xs">{api.description}</td>
                    <td className="p-3 text-center">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                        {api.count}
                      </span>
                    </td>
                    <td className="p-3">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-700 break-all">
                        {api.endpoint}
                      </code>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex gap-1 justify-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadCollection(api.collection)}
                          className="text-xs"
                          disabled={api.collection === 'fs.chunks'}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          JSON
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyApiUrl(api.endpoint)}
                          className="text-xs"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h4 className="font-semibold text-amber-800 mb-2">💡 Utilisation des API</h4>
            <p className="text-sm text-amber-700 mb-2">
              Toutes les API nécessitent une authentification admin. Exemple avec curl :
            </p>
            <code className="block text-xs bg-white p-2 rounded border text-gray-700 overflow-x-auto">
              curl -H "Authorization: Bearer VOTRE_TOKEN" {window.location.origin}/api/admin/backup/collection/users
            </code>
          </div>
        </CardContent>
      </Card>

      {/* Indicateur de progression */}
      {isLoading && progress.status && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-center mb-4">{progress.status}</h3>
            <div className="flex justify-center">
              <div className="flex space-x-1">
                <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
            <p className="text-center text-gray-500 mt-4 text-sm">
              Ne fermez pas cette page pendant l'opération...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;